import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import cytoscape, { Core } from 'cytoscape';
import { aiAPI, familyTreesAPI, peopleAPI, relationshipsAPI, usersAPI, FamilyTreeDetailedDto, PersonDto, RelationshipDto } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  EditableTreeTitle,
  AddPersonModal,
  AddRelationshipModal,
  EditPersonModal,
  MembersPanel,
  EditRelationshipModal,
  ExistingPersonPickerModal,
  AiCommandPanel,
  MobileCanvasHUD,
  MainLayout,
} from '../components';
import type { AddPersonSuccessResult } from '../components/AddPersonModal';
import type { EditPersonSuccessResult, PersonFamilyLink } from '../components/EditPersonModal';
import { loadPhotoAsDataUrl } from '../components/PhotoLoader';
import { CROP_CONFIG } from '../config/cropConfig';
import { brandClasses } from '../theme';
import { deriveCurrentUserTreeRole } from '../utils/memberManagement';
import { mergePersonForCanvasUpdate } from '../utils/canvasPersonMerge';
import { buildCanvasPersonLabel, getCanvasPersonVisualState } from '../utils/canvasPersonPresentation';
import {
  getPersonDisplayName,
  normalizeRelationshipType,
} from '../utils/relationshipLabels';
import { useRelationshipLabels } from '../utils/useRelationshipLabels';
import { useFormatDate } from '../i18n';
import { searchPeopleInOrder, getSearchSummary } from '../utils/canvasSearch';
import {
  ADD_PERSON_ACTION,
  ADD_RELATION_ACTION,
  EDIT_PERSON_ACTION,
  buildRightClickRelationshipDraft,
  evaluateMenuKeyNavigation,
  getCanvasContextMenuActions,
  getNodeActionTrayItems,
} from './CanvasPage.contextMenu';
import { evaluateCanvasDoubleTap, computeDoubleTapZoom, evaluateZoomKey, evaluateArrowKey, arrowDirectionToDelta, evaluateNodeTap, CanvasDoubleTapState } from './CanvasPage.interactions';
import { computeDragAngleDeg, inferRelationshipTypeFromAngle } from '../utils/relationshipDirection';
import maleDefaultImage from '../assets/plceholders/male-default.png';
import femaleDefaultImage from '../assets/plceholders/female-default.png';
import { useAppNavigation } from '../contexts/AppNavigationContext';
import { NODE_WIDTH, NODE_HEIGHT } from '../config/canvasStyles';

// Note: loadPhotoAsDataUrl is now imported from PhotoLoader utility
// This ensures consistent photo loading behavior across the app

const svgDataUrl = (svg: string) => `data:image/svg+xml;utf8,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}`;

const CANVAS_INTERACTION = {
  fitPaddingPx: 50,
  minZoom: 0.1,
  maxZoom: 5,
  zoomStepMultiplier: 1.2,
  animateDurationMs: 300,
  nodeDoubleTapThresholdMs: 320,
  canvasDoubleTapThresholdMs: 300,
  arrowNodeStepPx: 20,
  arrowPanStepPx: 80,
  viewportSaveDebounceMs: 1000,
  visiblePhotoLoadDebounceMs: 200,
  contextMenu: {
    widthPx: 220,
    emptyHeightPx: 60,
    personHeightPx: 104,
    viewportPaddingPx: 12,
  },
  fontSize: {
    basePx: 12,
    minPx: 7,
    maxPx: 20,
  },
  relationshipHandleSizePx: 12,
} as const;

const DEFAULT_AVATAR_DATA_URLS = {
  male: maleDefaultImage,
  female: femaleDefaultImage,
  unspecified: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" width="180" height="110" viewBox="0 0 180 110" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#f4f5fb"/>
          <stop offset="100%" stop-color="#eceef7"/>
        </linearGradient>
      </defs>
      <rect width="180" height="110" rx="16" fill="url(#bg)"/>
      <g transform="translate(6 2)">
        <circle cx="84" cy="36" r="17" fill="#8c8ca1"/>
        <path d="M61 106V76c0-13 10-23 23-23s23 10 23 23v30H61z" fill="#8c8ca1"/>
      </g>
    </svg>
  `),
} as const;

const getDefaultAvatarDataUrl = (gender?: string): string => {
  const genderLower = gender?.toLowerCase() || 'unspecified';

  if (genderLower === 'male') {
    return DEFAULT_AVATAR_DATA_URLS.male;
  }

  if (genderLower === 'female') {
    return DEFAULT_AVATAR_DATA_URLS.female;
  }

  return DEFAULT_AVATAR_DATA_URLS.unspecified;
};

// Keyed on normalizeRelationshipType() output so sorting works regardless of locale.
const RELATIONSHIP_PRIORITY: Record<string, number> = {
  spouse:      1,
  parent:      2,
  stepfather:  2,
  stepmother:  2,
  grandparent: 3,
  aunt:        4,
  uncle:       4,
  child:       5,
  stepchild:   5,
  grandchild:  6,
  niecenephew: 7,
  sibling:     8,
  stepsibling: 8,
  cousin:      9,
  inlaw:       10,
  other:       11,
};

const buildPhotoCacheKey = (
  photoUrl?: string,
  cropX?: number,
  cropY?: number,
  cropWidth?: number,
  cropHeight?: number,
  cropZoom?: number
): string => {
  if (!photoUrl) {
    return '';
  }

  return [
    photoUrl,
    cropX ?? '',
    cropY ?? '',
    cropWidth ?? '',
    cropHeight ?? '',
    cropZoom ?? '',
  ].join('|');
};

const ActionPaletteIcon: React.FC<{ kind: 'addPerson' | 'addRelationship' | 'zoomIn' | 'reset' | 'zoomOut' | 'fit' | 'members' | 'ai' | 'back' | 'grid' | 'snap' | 'publicTree' }> = ({ kind }) => {
  const iconClassName = 'w-7 h-7 drop-shadow-sm';

  switch (kind) {
    case 'addPerson':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      );
    case 'addRelationship':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.5 13.5l3-3" />
          <path d="M8.2 16.8l-1.4 1.4a3.25 3.25 0 01-4.6-4.6l3-3a3.25 3.25 0 014.6 0" />
          <path d="M14.4 7.4l1.4-1.4a3.25 3.25 0 114.6 4.6l-3 3a3.25 3.25 0 01-4.6 0" />
        </svg>
      );
    case 'zoomIn':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4 4" />
          <path d="M11 8.2v5.6" />
          <path d="M8.2 11h5.6" />
        </svg>
      );
    case 'reset':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 7H3v4" />
          <path d="M17 17h4v-4" />
          <path d="M4 11a8 8 0 0113.2-5.9L21 8" />
          <path d="M20 13a8 8 0 01-13.2 5.9L3 16" />
        </svg>
      );
    case 'zoomOut':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4 4" />
          <path d="M8.2 11h5.6" />
        </svg>
      );
    case 'fit':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 8V5a1 1 0 011-1h3" />
          <path d="M16 4h3a1 1 0 011 1v3" />
          <path d="M20 16v3a1 1 0 01-1 1h-3" />
          <path d="M8 20H5a1 1 0 01-1-1v-3" />
          <rect x="8" y="8" width="8" height="8" rx="1" />
        </svg>
      );
    case 'members':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM16.5 12a3 3 0 100-6 3 3 0 000 6zM3.5 19.5A4.5 4.5 0 018 15h2a4.5 4.5 0 014.5 4.5V20h-11z" />
          <path d="M13.5 19.8v-.3A5.8 5.8 0 0012 15.5h2.2a4.3 4.3 0 014.3 4.3z" />
        </svg>
      );
    case 'ai':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l1.9 4.9L19 10l-5.1 2.1L12 17l-1.9-4.9L5 10l5.1-2.1L12 3z" />
          <path d="M19 14l.8 2 2.2.8-2.2.8-.8 2-.8-2-2.2-.8 2.2-.8.8-2z" />
        </svg>
      );
    case 'back':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M11 18l-6-6 6-6" />
        </svg>
      );
    case 'grid':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'snap':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 9V7M12 17v-2M9 12H7M17 12h-2" />
        </svg>
      );
    case 'publicTree':
      return (
        <svg className={iconClassName} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
        </svg>
      );
  }
};

type CanvasContextMenuState = {
  isOpen: boolean;
  screenX: number;
  screenY: number;
  canvasX: number;
  canvasY: number;
  targetPersonId: string | null;
};

// Grid spacing in canvas units (pixels at zoom=1).
// A minor line every 40px and a major line every 200px (every 5 minor cells).
const GRID_MINOR_PX = 40;
const GRID_MAJOR_PX = GRID_MINOR_PX * 5; // 200

/**
 * Writes CSS background-* properties onto the Cytoscape container so the
 * grid tracks pan and zoom.  The offset math is equivalent to Cytoscape's
 * own canvas transform, so snapped nodes (multiples of GRID_MINOR_PX in
 * model space) always land exactly on a grid line.
 *
 * A node at model x = k*GRID_MINOR_PX appears at screen x = pan.x + k*minorSize.
 * The CSS background repeats every minorSize px starting at minorOffsetX = pan.x mod minorSize,
 * so the n-th line is at  minorOffsetX + n*minorSize = pan.x + (n - floor(pan.x/minorSize))*minorSize
 * which is exactly a multiple of minorSize offset by pan.x — matching the node position.
 */
function updateGridBackground(
  el: HTMLElement,
  show: boolean,
  zoom: number,
  pan: { x: number; y: number },
): void {
  if (!show) {
    clearGridBackground(el);
    return;
  }

  const minorSize = GRID_MINOR_PX * zoom;
  const majorSize = GRID_MAJOR_PX * zoom;

  const minorOffsetX = ((pan.x % minorSize) + minorSize) % minorSize;
  const minorOffsetY = ((pan.y % minorSize) + minorSize) % minorSize;
  const majorOffsetX = ((pan.x % majorSize) + majorSize) % majorSize;
  const majorOffsetY = ((pan.y % majorSize) + majorSize) % majorSize;

  const minor = 'rgba(180,168,150,0.45)';
  const major  = 'rgba(150,136,114,0.65)';
  const clear  = 'transparent';

  el.style.backgroundImage = [
    `linear-gradient(to right,  ${major} 1px, ${clear} 1px)`,
    `linear-gradient(to bottom, ${major} 1px, ${clear} 1px)`,
    `linear-gradient(to right,  ${minor} 1px, ${clear} 1px)`,
    `linear-gradient(to bottom, ${minor} 1px, ${clear} 1px)`,
  ].join(', ');

  el.style.backgroundSize = [
    `${majorSize}px ${majorSize}px`,
    `${majorSize}px ${majorSize}px`,
    `${minorSize}px ${minorSize}px`,
    `${minorSize}px ${minorSize}px`,
  ].join(', ');

  el.style.backgroundPosition = [
    `${majorOffsetX}px ${majorOffsetY}px`,
    `${majorOffsetX}px ${majorOffsetY}px`,
    `${minorOffsetX}px ${minorOffsetY}px`,
    `${minorOffsetX}px ${minorOffsetY}px`,
  ].join(', ');
}

function clearGridBackground(el: HTMLElement): void {
  el.style.backgroundImage = '';
  el.style.backgroundSize  = '';
  el.style.backgroundPosition = '';
}

// Apply crop data to a photo data URL and return cropped image
// Uses CROP_CONFIG for consistent sizing between modal and display
const applyCropToPhoto = async (
  photoDataUrl: string,
  cropX?: number,
  cropY?: number,
  cropWidth?: number,
  cropHeight?: number,
  cropZoom?: number
): Promise<string> => {
  // If no crop data, return original
  if (!cropZoom || cropZoom <= 0) {
    return photoDataUrl;
  }

  try {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(photoDataUrl);
          return;
        }


        // Calculate scaled image size at zoom level
        const scaledWidth = img.width * cropZoom;
        const scaledHeight = img.height * cropZoom;

        // Calculate crop region position in the scaled image (in PIXELS)
        const cropRegionX = (cropX || 0) * scaledWidth;
        const cropRegionY = (cropY || 0) * scaledHeight;

        // The actual crop box dimensions (in pixels of the scaled image)
        const cropBoxWidth = (cropWidth || 1) * scaledWidth;
        const cropBoxHeight = (cropHeight || 1) * scaledHeight;

        // Canvas should be sized to the actual crop box, not the modal size
        const canvasWidth = Math.round(cropBoxWidth);
        const canvasHeight = Math.round(cropBoxHeight);
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;


        // Fill background
        ctx.fillStyle = '#e5e7eb';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Position the image so crop region (cropRegionX, cropRegionY) appears at canvas (0,0)
        const drawX = -cropRegionX;
        const drawY = -cropRegionY;


        // Save context and draw
        ctx.save();

        // Draw rounded rectangle clip (this clips everything outside the rounded rectangle)
        ctx.beginPath();
        ctx.roundRect(0, 0, canvasWidth, canvasHeight, CROP_CONFIG.NODE_BORDER_RADIUS);
        ctx.clip();

        // Draw the image at 1:1 scale
        // The drawX/drawY position the image so the crop region (cropX, cropY) appears at canvas (0,0)
        // The canvas size matches the crop box, so the entire canvas shows the cropped region
        ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);

        ctx.restore();


        resolve(canvas.toDataURL());
      };
      img.onerror = () => {
        resolve(photoDataUrl);
      };
      img.src = photoDataUrl;
    });
  } catch (error) {
    return photoDataUrl;
  }
};

export const CanvasPage: React.FC = () => {
  const { t } = useTranslation();
  const { getCanvasEdgeSemantics, getRelativeLabelForPerson } = useRelationshipLabels();
  const { treeId } = useParams<{ treeId: string }>();
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const treeRef = useRef<FamilyTreeDetailedDto | null>(null);  // Keep tree in ref for closures
  const [tree, setTree] = useState<FamilyTreeDetailedDto | null>(null);
  const [treeDescription, setTreeDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<PersonDto | null>(null);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [showEditPerson, setShowEditPerson] = useState(false);
  const [showExistingPersonPicker, setShowExistingPersonPicker] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [isAiEnabled, setIsAiEnabled] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [snapGrid, setSnapGrid] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Keep a ref in sync so Cytoscape event handlers can read showGrid without stale closure
  useEffect(() => {
    showGridRef.current = showGrid;
    if (cyRef.current && containerRef.current) {
      updateGridBackground(containerRef.current, showGrid, cyRef.current.zoom(), cyRef.current.pan());
    } else if (containerRef.current && !showGrid) {
      clearGridBackground(containerRef.current);
    }
  }, [showGrid]);
  const [selectedRelationship, setSelectedRelationship] = useState<RelationshipDto | null>(null);
  const [showEditRelationshipModal, setShowEditRelationshipModal] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [photoDataUrls, setPhotoDataUrls] = useState<{ [key: string]: string }>({});
  const [, setHandleOverlayVersion] = useState(0);
  const [relationshipDraft, setRelationshipDraft] = useState<{
    sourcePersonId: string;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
  } | null>(null);
  const [hoveredRelationshipTargetId, setHoveredRelationshipTargetId] = useState<string | null>(null);
  const [pendingRelationshipPeople, setPendingRelationshipPeople] = useState<{
    personAId: string;
    personBId: string;
  } | null>(null);
  const [pendingRelationshipType, setPendingRelationshipType] = useState<string | null>(null);
  const [pendingRelatedPersonSourceId, setPendingRelatedPersonSourceId] = useState<string | null>(null);
  const [returnToEditPersonAfterAddRelationship, setReturnToEditPersonAfterAddRelationship] = useState(false);
  const [returnToEditPersonAfterAddPerson, setReturnToEditPersonAfterAddPerson] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearchResults, setAiSearchResults] = useState<PersonDto[] | null>(null);
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [searchResultIndex, setSearchResultIndex] = useState(0);
  const [contextMenu, setContextMenu] = useState<CanvasContextMenuState>({
    isOpen: false,
    screenX: 0,
    screenY: 0,
    canvasX: 0,
    canvasY: 0,
    targetPersonId: null,
  });
  const [pendingAddPersonPosition, setPendingAddPersonPosition] = useState<{ x: number; y: number } | null>(null);
  const viewportSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showGridRef = useRef(false);
  const snapGridRef = useRef(false);
  const isCtrlHeldRef = useRef(false);
  const photoDataUrlsRef = useRef(photoDataUrls);
  const pendingPhotoLoadsRef = useRef<Set<string>>(new Set());
  const appliedPhotoDataUrlsRef = useRef<{ [key: string]: string }>({});
  const zoomFontSizeRef = useRef<number | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const lastPersistedTreeIdRef = useRef<string | null>(null);
  const lastTappedNodeRef = useRef<{ nodeId: string; timestamp: number } | null>(null);
  const lastCanvasTapRef = useRef<CanvasDoubleTapState | null>(null);
  const lastAutoFocusedSearchSignatureRef = useRef<string>('');
  const selectedPersonRef = useRef<typeof selectedPerson>(null);
  const navigate = useNavigate();
  const { user, updateCurrentUser } = useAuth();
  const { setNavigationState, clearNavigationState } = useAppNavigation();

  const isBlankCanvas = !tree || !tree.people || tree.people.length === 0;
  const canAddPeople = Boolean(tree?.canAddPerson);
  const canCreateRelationships = Boolean(tree?.canAddRelationship);
  const canManageMembers = Boolean(tree?.canManageMembers);
  const canManagePublicTree = tree?.role === 'Owner' || tree?.role === 'Manager' || tree?.role === 'Editor';
  const isRightSidebarVisible = Boolean(selectedPerson || showMembersPanel || (isAiEnabled && showAiPanel));
  const textSearchResults = useMemo(() => searchPeopleInOrder(tree?.people ?? [], searchQuery), [tree?.people, searchQuery]);
  const activeSearchResults = aiSearchResults ?? textSearchResults;
  const activeSearchQuery = aiSearchResults ? aiSearchQuery : searchQuery;
  const activeSearchIds = useMemo(() => new Set(activeSearchResults.map((person) => person.id)), [activeSearchResults]);
  const activeSearchCount = activeSearchResults.length;

  // Track viewport width to switch between sidebar (desktop) and bottom sheet (mobile)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // On mobile the sidebar is used for members and AI; person details go to the bottom sheet.
  const isDesktopSidebarVisible = isMobile ? (showMembersPanel || (isAiEnabled && showAiPanel)) : isRightSidebarVisible;
  const getContextMenuActions = React.useCallback((targetPersonId: string | null, sourceTree?: FamilyTreeDetailedDto | null) => {
    const activeTree = sourceTree ?? tree;
    const targetPerson = targetPersonId
      ? activeTree?.people.find((person) => person.id === targetPersonId) ?? null
      : null;

    return getCanvasContextMenuActions(targetPersonId, {
      canAddPerson: Boolean(activeTree?.canAddPerson),
      canAddRelationship: Boolean(activeTree?.canAddRelationship),
      canEditPerson: Boolean(targetPerson?.canEdit),
    });
  }, [tree]);

  const selectedPersonFamilyLinks = useMemo<PersonFamilyLink[]>(() => {
    if (!tree || !selectedPerson) {
      return [];
    }

    return tree.relationships
      .filter((relationship) => relationship.personAId === selectedPerson.id || relationship.personBId === selectedPerson.id)
      .map((relationship) => {
        const relatedPersonId =
          relationship.personAId === selectedPerson.id ? relationship.personBId : relationship.personAId;
        const relatedPerson = tree.people.find((person) => person.id === relatedPersonId);

        return {
          relationshipId: relationship.id,
          relatedPersonId,
          relatedPersonName: getPersonDisplayName(relatedPerson),
          relationshipType: relationship.type ?? 'other',
          relationToPerson: getRelativeLabelForPerson(relationship, selectedPerson.id, relatedPerson),
          canEdit: Boolean(relationship.canEdit),
          canDelete: Boolean(relationship.canDelete),
        };
      })
      .sort((left, right) => {
        const leftPriority = RELATIONSHIP_PRIORITY[normalizeRelationshipType(left.relationshipType)] ?? 999;
        const rightPriority = RELATIONSHIP_PRIORITY[normalizeRelationshipType(right.relationshipType)] ?? 999;

        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }

        return left.relatedPersonName.localeCompare(right.relatedPersonName);
      });
  }, [selectedPerson, tree, getRelativeLabelForPerson]);

  const openMembersPanel = React.useCallback(() => {
    setSelectedPerson(null);
    setShowAiPanel(false);
    setShowMembersPanel(true);
  }, []);

  const openAiPanel = React.useCallback(() => {
    if (!isAiEnabled) {
      return;
    }

    setSelectedPerson(null);
    setShowMembersPanel(false);
    setShowAiPanel(true);
  }, [isAiEnabled]);

  const openPersonPanel = React.useCallback((person: PersonDto) => {
    setShowMembersPanel(false);
    setShowAiPanel(false);
    setSelectedPerson(person);
  }, []);

  const openAddPersonModal = React.useCallback(() => {
    setPendingAddPersonPosition(null);
    setShowAddPerson(true);
  }, []);

  const openAddRelationshipModal = React.useCallback(() => {
    setPendingRelationshipPeople(null);
    setPendingRelationshipType(null);
    setShowAddRelationship(true);
  }, []);

  const clearAiSearch = React.useCallback(() => {
    setAiSearchResults(null);
    setAiSearchQuery('');
    setSearchResultIndex(0);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAiConfig = async () => {
      try {
        const response = await aiAPI.getConfig();
        if (!isMounted) {
          return;
        }

        const enabled = Boolean(response.data?.enabled);
        setIsAiEnabled(enabled);
        if (!enabled) {
          setShowAiPanel(false);
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setIsAiEnabled(false);
        setShowAiPanel(false);
      }
    };

    loadAiConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const focusSearchResult = React.useCallback((person: PersonDto | null) => {
    if (!person || !cyRef.current) {
      return;
    }

    const node = cyRef.current.getElementById(person.id);
    if (node.length === 0) {
      return;
    }

    cyRef.current.animate({
      fit: { eles: node, padding: 120 },
      duration: CANVAS_INTERACTION.animateDurationMs,
    });
  }, []);

  const setSearchResultsFromAi = React.useCallback((query: string, people: PersonDto[]) => {
    setSearchQuery(query);
    setAiSearchQuery(query);
    setAiSearchResults(people);
    setSearchResultIndex(0);
  }, []);

  const stepSearchResult = React.useCallback((delta: number) => {
    const total = activeSearchResults.length;
    if (total === 0) {
      return;
    }

    const nextIndex = (searchResultIndex + delta + total) % total;
    setSearchResultIndex(nextIndex);
    focusSearchResult(activeSearchResults[nextIndex]);
  }, [activeSearchResults, focusSearchResult, searchResultIndex]);

  const refreshCurrentTree = useCallback(async () => {
    if (!treeId) {
      return;
    }

    try {
      const response = await familyTreesAPI.getTree(treeId);
      if (response.data.statusCode === 200) {
        const treeData = response.data.data;
        treeRef.current = treeData;
        setTree(treeData);
        setTreeDescription(treeData.description ?? '');
      }
    } catch (err) {
    }
  }, [treeId]);

  useEffect(() => {
    photoDataUrlsRef.current = photoDataUrls;
  }, [photoDataUrls]);

  const closeContextMenu = React.useCallback(() => {
    setContextMenu((currentMenu) => {
      if (!currentMenu.isOpen) {
        return currentMenu;
      }

      return {
        ...currentMenu,
        isOpen: false,
        targetPersonId: null,
      };
    });
  }, []);

  useEffect(() => {
    if (!contextMenu.isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (contextMenuRef.current && targetNode && contextMenuRef.current.contains(targetNode)) {
        return;
      }

      closeContextMenu();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeContextMenu();
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [closeContextMenu, contextMenu.isOpen]);

  // Move focus into the context menu when it opens so keyboard users can
  // navigate immediately without a mouse click.
  useEffect(() => {
    if (!contextMenu.isOpen || !contextMenuRef.current) return;
    const firstItem = contextMenuRef.current.querySelector<HTMLButtonElement>('[role="menuitem"]');
    firstItem?.focus();
  }, [contextMenu.isOpen]);

  // Keep selectedPersonRef in sync so arrow-key handler can read current value
  useEffect(() => {
    selectedPersonRef.current = selectedPerson;
  }, [selectedPerson]);

  // Keyboard zoom shortcuts: +/= zoom in, - zoom out, 0 reset to 100%, F fit all nodes.
  // Suppressed when typing in a text field, when focus is inside a modal,
  // or when any panel/modal is currently open.
  useEffect(() => {
    const handleZoomKey = (event: KeyboardEvent) => {
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase() ?? '';
      const isEditingText =
        tag === 'input' || tag === 'textarea' || (el as HTMLElement | null)?.isContentEditable === true;
        const isInsideModal = !!(el?.closest('[role="dialog"], dialog'));
        const isAnyModalOpen =
          showAddPerson || showEditPerson || showAddRelationship || showEditRelationshipModal ||
          showExistingPersonPicker || showMembersPanel || (isAiEnabled && showAiPanel);

      const action = evaluateZoomKey(event.key, isEditingText || isInsideModal || isAnyModalOpen);
      if (!action) return;

      event.preventDefault();
      if (action === 'zoomIn') zoomIn();
      else if (action === 'zoomOut') zoomOut();
      else if (action === 'reset') resetZoom();
      else if (action === 'fit') void fitCanvas();
    };

    window.addEventListener('keydown', handleZoomKey);
    return () => window.removeEventListener('keydown', handleZoomKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAiEnabled, showAddPerson, showEditPerson, showAddRelationship, showEditRelationshipModal, showExistingPersonPicker, showMembersPanel, showAiPanel]);

  // Keep snapGridRef in sync
  useEffect(() => {
    snapGridRef.current = snapGrid;
  }, [snapGrid]);

  // Grid toggle shortcut: G (suppressed while typing or any modal is open)
  useEffect(() => {
    const handleGridKey = (event: KeyboardEvent) => {
      if (event.key !== 'g' && event.key !== 'G') return;
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase() ?? '';
      const isEditingText =
        tag === 'input' || tag === 'textarea' || (el as HTMLElement | null)?.isContentEditable === true;
        const isInsideModal = !!(el?.closest('[role="dialog"], dialog'));
        const isAnyModalOpen =
          showAddPerson || showEditPerson || showAddRelationship || showEditRelationshipModal ||
          showExistingPersonPicker || showMembersPanel || (isAiEnabled && showAiPanel);
      if (isEditingText || isInsideModal || isAnyModalOpen) return;
      event.preventDefault();
      setShowGrid((prev) => !prev);
    };
    window.addEventListener('keydown', handleGridKey);
    return () => window.removeEventListener('keydown', handleGridKey);
  }, [isAiEnabled, showAddPerson, showEditPerson, showAddRelationship, showEditRelationshipModal, showExistingPersonPicker, showMembersPanel, showAiPanel]);

  // Snap-to-grid toggle shortcut: S (suppressed while typing or any modal is open)
  useEffect(() => {
    const handleSnapKey = (event: KeyboardEvent) => {
      if (event.key !== 's' && event.key !== 'S') return;
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase() ?? '';
      const isEditingText =
        tag === 'input' || tag === 'textarea' || (el as HTMLElement | null)?.isContentEditable === true;
        const isInsideModal = !!(el?.closest('[role="dialog"], dialog'));
        const isAnyModalOpen =
          showAddPerson || showEditPerson || showAddRelationship || showEditRelationshipModal ||
          showExistingPersonPicker || showMembersPanel || (isAiEnabled && showAiPanel);
      if (isEditingText || isInsideModal || isAnyModalOpen) return;
      event.preventDefault();
      setSnapGrid((prev) => !prev);
    };
    window.addEventListener('keydown', handleSnapKey);
    return () => window.removeEventListener('keydown', handleSnapKey);
  }, [isAiEnabled, showAddPerson, showEditPerson, showAddRelationship, showEditRelationshipModal, showExistingPersonPicker, showMembersPanel, showAiPanel]);

  // Multi-select: track Ctrl key to switch between pan and rubber-band box selection.
  // Ctrl/Meta down  → enable box selection, disable canvas pan.
  // Ctrl/Meta up    → disable box selection, re-enable canvas pan.
  // Escape          → deselect all nodes.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        isCtrlHeldRef.current = true;
        if (cyRef.current) {
          // Enable rubber-band box selection while keeping panning enabled.
          // Cytoscape resolves the ambiguity: a mousedown on a node fires tap;
          // a mousedown on empty canvas with boxSelection on draws the rectangle.
          cyRef.current.boxSelectionEnabled(true);
        }
      }
      if (event.key === 'Escape') {
        cyRef.current?.nodes(':selected').unselect();
        setShowBulkDeleteConfirm(false);
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        isCtrlHeldRef.current = false;
        if (cyRef.current) {
          cyRef.current.boxSelectionEnabled(false);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  // Load tree data
  useEffect(() => {
    if (!treeId) {
      setError('Tree ID not provided');
      setIsLoading(false);
      return;
    }

    const loadTree = async () => {
      try {
        setIsLoading(true);
        const response = await familyTreesAPI.getTree(treeId);

        if (response.data.statusCode === 200) {
          const treeData = response.data.data;
          treeRef.current = treeData;  // Keep ref updated
          setTree(treeData);
          setTreeDescription(treeData.description ?? '');
        }
      } catch (err) {
        setError('Failed to load family tree');
      } finally {
        setIsLoading(false);
      }
    };

    loadTree();
  }, [treeId]);

  useEffect(() => {
    if (!treeId || !user || user.lastOpenedTreeId === treeId || lastPersistedTreeIdRef.current === treeId) {
      return;
    }

    let isCancelled = false;

    const persistLastOpenedTree = async () => {
      try {
        await usersAPI.updateProfile({ lastOpenedTreeId: treeId });
        if (!isCancelled) {
          lastPersistedTreeIdRef.current = treeId;
          updateCurrentUser({ lastOpenedTreeId: treeId });
        }
      } catch (err) {
      }
    };

    persistLastOpenedTree();

    return () => {
      isCancelled = true;
    };
  }, [treeId, updateCurrentUser, user]);

  useEffect(() => {
    setNavigationState({
      activeTreeId: treeId,
      canManageMembers,
      canManagePublicTree,
    });

    return () => {
      clearNavigationState();
    };
  }, [canManageMembers, canManagePublicTree, clearNavigationState, setNavigationState, treeId]);

  // Lazy load individual photos as they're requested (on-demand)
  // PhotoLoader handles caching internally, so we don't need to check cache here
  const loadPhotoLazy = React.useCallback(async (
    photoUrl: string,
    personId?: string,
    cropX?: number,
    cropY?: number,
    cropWidth?: number,
    cropHeight?: number,
    cropZoom?: number
  ) => {
    const photoCacheKey = buildPhotoCacheKey(photoUrl, cropX, cropY, cropWidth, cropHeight, cropZoom);
    const cachedPhotoDataUrl = photoDataUrlsRef.current[photoCacheKey];
    if (cachedPhotoDataUrl) {
      if (personId && cyRef.current) {
        const node = cyRef.current.getElementById(personId);
        if (node && node.data('photoUrl') !== cachedPhotoDataUrl) {
          node.data('photoUrl', cachedPhotoDataUrl);
        }
      }
      return;
    }

    if (pendingPhotoLoadsRef.current.has(photoCacheKey)) {
      return;
    }

    pendingPhotoLoadsRef.current.add(photoCacheKey);

    try {
      let dataUrl = await loadPhotoAsDataUrl(photoUrl);
      if (dataUrl) {

        // Apply crop data if available
        if (cropZoom && cropZoom > 0) {
          dataUrl = await applyCropToPhoto(dataUrl, cropX, cropY, cropWidth, cropHeight, cropZoom);
        }

        // Also update React state for consistency
        setPhotoDataUrls(prev => {
          if (prev[photoCacheKey] === dataUrl) {
            return prev;
          }

          return {
            ...prev,
            [photoCacheKey]: dataUrl
          };
        });

        // Update the Cytoscape node with the new photo immediately
        if (personId && cyRef.current) {
          const node = cyRef.current.getElementById(personId);
          if (node) {
            node.data('photoUrl', dataUrl);
          }
        }
      }
    } catch (error) {
    } finally {
      pendingPhotoLoadsRef.current.delete(photoCacheKey);
    }
  }, []);

  const updateTreeState = React.useCallback((updater: (currentTree: FamilyTreeDetailedDto) => FamilyTreeDetailedDto) => {
    setTree((currentTree) => {
      if (!currentTree) {
        return currentTree;
      }

      const updatedTree = updater(currentTree);
      treeRef.current = updatedTree;
      return updatedTree;
    });
  }, []);

  const updatePersonInCanvas = React.useCallback((updatedPerson: PersonDto, photoChanged: boolean) => {
    const previousPerson = treeRef.current?.people.find((person) => person.id === updatedPerson.id);
    const mergedPerson = mergePersonForCanvasUpdate(previousPerson ?? selectedPerson, updatedPerson);

    updateTreeState((currentTree) => ({
      ...currentTree,
      people: currentTree.people.map((person) => {
        return person.id === updatedPerson.id ? mergedPerson : person;
      }),
    }));

    setSelectedPerson((currentPerson) => {
      return currentPerson?.id === updatedPerson.id ? mergedPerson : currentPerson;
    });

    const fallbackPhotoUrl = getDefaultAvatarDataUrl(mergedPerson.gender);
    const visualState = getCanvasPersonVisualState(mergedPerson);
    const photoCacheKey = buildPhotoCacheKey(
      mergedPerson.photoUrl,
      mergedPerson.photoCropX,
      mergedPerson.photoCropY,
      mergedPerson.photoCropWidth,
      mergedPerson.photoCropHeight,
      mergedPerson.photoCropZoom
    );
    const cachedPhotoDataUrl = photoCacheKey ? photoDataUrlsRef.current[photoCacheKey] : '';
    const nodePhotoUrl = cachedPhotoDataUrl || fallbackPhotoUrl;

    if (photoChanged) {
      const previousPhotoCacheKey = buildPhotoCacheKey(
        previousPerson?.photoUrl,
        previousPerson?.photoCropX,
        previousPerson?.photoCropY,
        previousPerson?.photoCropWidth,
        previousPerson?.photoCropHeight,
        previousPerson?.photoCropZoom
      );

      if (previousPhotoCacheKey || photoCacheKey) {
        setPhotoDataUrls((prev) => {
          const updated = { ...prev };
          if (previousPhotoCacheKey) {
            delete updated[previousPhotoCacheKey];
          }
          if (photoCacheKey) {
            delete updated[photoCacheKey];
          }
          return updated;
        });

        if (previousPhotoCacheKey) {
          delete appliedPhotoDataUrlsRef.current[previousPhotoCacheKey];
          pendingPhotoLoadsRef.current.delete(previousPhotoCacheKey);
        }

        if (photoCacheKey) {
          delete appliedPhotoDataUrlsRef.current[photoCacheKey];
          pendingPhotoLoadsRef.current.delete(photoCacheKey);
        }
      }
    }

    if (cyRef.current) {
      const node = cyRef.current.getElementById(mergedPerson.id);
      if (node) {
        node.data({
          label: buildCanvasPersonLabel(mergedPerson),
          firstName: mergedPerson.firstName,
          lastName: mergedPerson.lastName,
          gender: mergedPerson.gender || 'Unspecified',
          borderColor: visualState.borderColor,
          borderStyle: visualState.borderStyle,
          statusTextColor: visualState.statusTextColor,
          backgroundBlacken: visualState.backgroundBlacken,
          nodeOpacity: visualState.opacity,
          isDeceased: visualState.isDeceased,
          fallbackPhotoUrl,
          backgroundFit: 'cover',
          backgroundOpacity: 1,
          originalPhotoUrl: mergedPerson.photoUrl,
          photoCacheKey,
          photoCropX: mergedPerson.photoCropX,
          photoCropY: mergedPerson.photoCropY,
          photoCropWidth: mergedPerson.photoCropWidth,
          photoCropHeight: mergedPerson.photoCropHeight,
          photoCropZoom: mergedPerson.photoCropZoom,
          photoUrl: photoChanged ? fallbackPhotoUrl : nodePhotoUrl,
        });
      }
    }

    if (mergedPerson.photoUrl) {
      loadPhotoLazy(
        mergedPerson.photoUrl,
        mergedPerson.id,
        mergedPerson.photoCropX,
        mergedPerson.photoCropY,
        mergedPerson.photoCropWidth,
        mergedPerson.photoCropHeight,
        mergedPerson.photoCropZoom
      );
    }
  }, [loadPhotoLazy, selectedPerson, updateTreeState]);

  const handleEditPersonSuccess = React.useCallback(async (result: EditPersonSuccessResult) => {
    setShowEditPerson(false);

    if (!result.didChange) {
      return;
    }

    if (treeId) {
      try {
        const response = await familyTreesAPI.getTree(treeId);
        if (response.data.statusCode === 200) {
          const refreshedTree = response.data.data;
          treeRef.current = refreshedTree;
          setTree(refreshedTree);
          setSelectedPerson(refreshedTree.people.find((person) => person.id === result.person.id) ?? null);
          return;
        }
      } catch (err) {
      }
    }

    updatePersonInCanvas(result.person, result.photoChanged);
  }, [treeId, updatePersonInCanvas]);

  const openRelationshipOnCanvas = React.useCallback((relationshipId: string) => {
    const currentTree = treeRef.current;
    const relationship = currentTree?.relationships.find((item) => item.id === relationshipId);
    if (!relationship) {
      return;
    }

    setShowEditPerson(false);
    setSelectedRelationship(relationship);
    setShowEditRelationshipModal(true);

    if (cyRef.current) {
      const edge = cyRef.current.getElementById(relationshipId);
      if (edge && !edge.empty()) {
        cyRef.current.animate({
          fit: {
            eles: edge.closedNeighborhood(),
            padding: 120,
          },
          duration: CANVAS_INTERACTION.animateDurationMs,
        });
      }
    }
  }, []);

  const reopenEditPersonFromTree = React.useCallback((nextTree?: FamilyTreeDetailedDto) => {
    const sourceTree = nextTree ?? treeRef.current;
    if (!sourceTree || !selectedPerson) {
      return;
    }

    const refreshedSelectedPerson = sourceTree.people.find((person) => person.id === selectedPerson.id);
    if (!refreshedSelectedPerson) {
      return;
    }

    setSelectedPerson(refreshedSelectedPerson);
    setShowEditPerson(true);
  }, [selectedPerson]);

  const getSelectedNodeHandlePoints = React.useCallback(() => {
    if (!selectedPerson || !cyRef.current) {
      return [];
    }

    const node = cyRef.current.getElementById(selectedPerson.id);
    if (!node || node.empty()) {
      return [];
    }

    const box = node.renderedBoundingBox({
      includeLabels: false,
      includeOverlays: false,
    });
    const midX = (box.x1 + box.x2) / 2;
    const midY = (box.y1 + box.y2) / 2;

    return [
      { key: 'top', x: midX, y: box.y1 },
      { key: 'right', x: box.x2, y: midY },
      { key: 'bottom', x: midX, y: box.y2 },
      { key: 'left', x: box.x1, y: midY },
    ];
  }, [selectedPerson]);

  const findNodeAtRenderedPoint = React.useCallback((x: number, y: number, sourcePersonId: string) => {
    if (!cyRef.current) {
      return null;
    }

    const matchingNode = cyRef.current.nodes().toArray().find((node: any) => {
      if (node.id() === sourcePersonId) {
        return false;
      }

      const box = node.renderedBoundingBox({ includeLabels: false, includeOverlays: false });
      return x >= box.x1 && x <= box.x2 && y >= box.y1 && y <= box.y2;
    });

    return matchingNode || null;
  }, []);

  const beginRelationshipDraft = React.useCallback((sourcePersonId: string, startX: number, startY: number) => {
    setRelationshipDraft({
      sourcePersonId,
      startX,
      startY,
      currentX: startX,
      currentY: startY,
    });
  }, []);

  const saveNodePosition = React.useCallback(async (personId: string, x: number, y: number) => {
    if (!treeId || !cyRef.current) return;

    try {
      await familyTreesAPI.saveLayout(treeId, { positions: [{ personId, x, y }] });
    } catch (err) {
    }
  }, [treeId]);

  const loadCanvasViewport = React.useCallback(async () => {
    if (!treeId) {
      return;
    }
    if (!cyRef.current) {
      return;
    }

    try {
      const response = await familyTreesAPI.getCanvasViewport(treeId);
      const viewport = response.data.data;

      if (!viewport) {
        cyRef.current.fit(undefined, CANVAS_INTERACTION.fitPaddingPx);
        return;
      }

      const { zoomLevel, panX, panY } = viewport;

      if (
        typeof zoomLevel !== 'number' ||
        isNaN(zoomLevel) ||
        zoomLevel < CANVAS_INTERACTION.minZoom ||
        zoomLevel > CANVAS_INTERACTION.maxZoom
      ) {
        cyRef.current.fit(undefined, CANVAS_INTERACTION.fitPaddingPx);
        return;
      }

      cyRef.current.zoom(zoomLevel);
      cyRef.current.pan({ x: panX, y: panY });
    } catch (error) {
      if (cyRef.current && cyRef.current.nodes().length > 0) {
        cyRef.current.fit(undefined, CANVAS_INTERACTION.fitPaddingPx);
      }
    }
  }, [treeId]);

  const saveCanvasViewport = React.useCallback(async () => {
    if (!treeId) {
      return;
    }
    if (!cyRef.current) {
      return;
    }
    if (viewportSaveTimeoutRef.current) {
      clearTimeout(viewportSaveTimeoutRef.current);
    }

    viewportSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const zoomLevel = Math.min(
          CANVAS_INTERACTION.maxZoom,
          Math.max(CANVAS_INTERACTION.minZoom, cyRef.current!.zoom())
        );
        const pan = cyRef.current!.pan();

        await familyTreesAPI.saveCanvasViewport(treeId, {
          zoomLevel,
          panX: pan.x,
          panY: pan.y,
        });
      } catch (error) {
      } finally {
        viewportSaveTimeoutRef.current = null;
      }
    }, CANVAS_INTERACTION.viewportSaveDebounceMs);
  }, [treeId]);

  // Arrow key navigation.
  // - Person selected + canMoveNodes → move the node by arrowNodeStepPx and save position.
  // - No person selected → pan the canvas by arrowPanStepPx.
  // Suppressed while typing in inputs/textareas.
  useEffect(() => {
    const handleArrowKey = (event: KeyboardEvent) => {
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase() ?? '';
      const isEditingText =
        tag === 'input' || tag === 'textarea' || (el as HTMLElement | null)?.isContentEditable === true;

      const direction = evaluateArrowKey(event.key, isEditingText);
      if (!direction || !cyRef.current) return;

      event.preventDefault();

      const cy = cyRef.current;
      const person = selectedPersonRef.current;

      if (person && treeRef.current?.canMoveNodes) {
        const node = cy.getElementById(person.id);
        if (node.length === 0) return;
        const { dx, dy } = arrowDirectionToDelta(direction, CANVAS_INTERACTION.arrowNodeStepPx);
        const pos = node.position();
        const newX = pos.x + dx;
        const newY = pos.y + dy;
        node.position({ x: newX, y: newY });
        void saveNodePosition(person.id, newX, newY);
      } else {
        const { dx, dy } = arrowDirectionToDelta(direction, CANVAS_INTERACTION.arrowPanStepPx);
        const pan = cy.pan();
        cy.pan({ x: pan.x + dx, y: pan.y + dy });
        void saveCanvasViewport();
      }
    };

    window.addEventListener('keydown', handleArrowKey);
    return () => window.removeEventListener('keydown', handleArrowKey);
  }, [saveNodePosition, saveCanvasViewport]);

  // Initialize Cytoscape immediately when tree loads (lazy load photos on demand)
  useEffect(() => {
    if (!tree) {
      return;
    }

    let viewportUpdateTimeout: NodeJS.Timeout | null = null;
    let initialViewportLoadTimeout: NodeJS.Timeout | null = null;
    let zoomFontUpdateFrame: number | null = null;
    let containerElement: HTMLDivElement | null = null;
    let handleNativeContextMenu: ((event: MouseEvent) => void) | null = null;

    // Use requestAnimationFrame to ensure DOM is ready
    const frameId = requestAnimationFrame(() => {
      if (!containerRef.current) {
        return;
      }

      // Prepare nodes from people - with default grid spacing if no saved positions
      const nodes = tree.people.map((person, index) => {
        // If person has saved position, use it; otherwise use grid layout
        let x: number = person.x ?? 500;
        let y: number = person.y ?? 500;

        if (x === 500 && y === 500) {
          // Default grid: arrange in 4 columns, spacing them out
          const colIndex = index % 4;
          const rowIndex = Math.floor(index / 4);
          x = 200 + colIndex * 300;
          y = 200 + rowIndex * 300;
        }

        // For lazy loading: only use data URL if already loaded, otherwise no photo URL
        // This prevents 404 errors on initial render
        const fallbackPhotoUrl = getDefaultAvatarDataUrl(person.gender);
        const photoCacheKey = buildPhotoCacheKey(
          person.photoUrl,
          person.photoCropX,
          person.photoCropY,
          person.photoCropWidth,
          person.photoCropHeight,
          person.photoCropZoom
        );
        const displayPhotoUrl = person.photoUrl && photoDataUrlsRef.current[photoCacheKey]
          ? photoDataUrlsRef.current[photoCacheKey]
          : fallbackPhotoUrl;

        const visualState = getCanvasPersonVisualState(person);

        return {
          data: {
            id: person.id,
            label: `${person.isDeceased ? '† ' : ''}${person.firstName}\n${person.lastName}`,
            personId: person.id,
            photoUrl: displayPhotoUrl,
            photoCacheKey,
            fallbackPhotoUrl,
            backgroundFit: 'cover',
            backgroundOpacity: 1,
            originalPhotoUrl: person.photoUrl, // Store original for lazy loading later
            firstName: person.firstName,
            lastName: person.lastName,
            gender: person.gender || 'Unspecified',
            borderColor: visualState.borderColor,
            borderStyle: visualState.borderStyle,
            statusTextColor: visualState.statusTextColor,
            backgroundBlacken: visualState.backgroundBlacken,
            nodeOpacity: visualState.opacity,
            isDeceased: visualState.isDeceased,
            // Crop data for photo positioning
            photoCropX: person.photoCropX,
            photoCropY: person.photoCropY,
            photoCropWidth: person.photoCropWidth,
            photoCropHeight: person.photoCropHeight,
            photoCropZoom: person.photoCropZoom,
          },
          position: {
            x,
            y,
          },
        };
      });

      nodes.forEach((node) => {
        const sourcePerson = tree.people.find((person) => person.id === node.data.id);
        if (!sourcePerson) {
          return;
        }

        const visualState = getCanvasPersonVisualState(sourcePerson);
        node.data.label = buildCanvasPersonLabel(sourcePerson);
        node.data.borderColor = visualState.borderColor;
        node.data.borderStyle = visualState.borderStyle;
        node.data.statusTextColor = visualState.statusTextColor;
        node.data.backgroundBlacken = visualState.backgroundBlacken;
        node.data.nodeOpacity = visualState.opacity;
        node.data.isDeceased = visualState.isDeceased;
      });

      // Prepare edges from relationships
      // Only include edges where both source and target nodes exist
      const nodeIds = new Set(nodes.map((n) => n.data.id));
      const edges = tree.relationships
        .filter((rel) => nodeIds.has(rel.personAId) && nodeIds.has(rel.personBId))
        .map((rel) => {
          const personB = tree.people.find((person) => person.id === rel.personBId);
          const edgeSemantics = getCanvasEdgeSemantics(rel.type, personB);

          return {
            data: {
              id: rel.id,
              source: rel.personAId,
              target: rel.personBId,
              label: edgeSemantics.label,
              sourceArrowShape: edgeSemantics.sourceArrowShape,
              targetArrowShape: edgeSemantics.targetArrowShape,
              lineStyle: edgeSemantics.lineStyle,
            },
          };
        });

      // Log skipped relationships
      const skippedRels = tree.relationships.filter((rel) => !nodeIds.has(rel.personAId) || !nodeIds.has(rel.personBId));
      if (skippedRels.length > 0) {
      }

      // Initialize Cytoscape
      const cy = cytoscape({
        container: containerRef.current,
        elements: [...nodes, ...edges],
        style: [
          {
            selector: 'node',
            style: {
              'background-color': '#e5e7eb',
              'background-image': 'data(photoUrl)',
              'background-fit': (element: any) => element.data('backgroundFit') || 'cover',
              'background-opacity': (element: any) => element.data('backgroundOpacity') ?? 1,
              'background-blacken': (element: any) => element.data('backgroundBlacken') ?? 0,
              'background-repeat': 'no-repeat',
              'background-position-x': '50%',
              'background-position-y': '50%',
              'background-clip': 'node',
              'label': 'data(label)',
              'text-valign': 'bottom',
              'text-halign': 'center',
              'text-margin-y': 12,
              'color': 'data(statusTextColor)',
              'width': '180px',
              'height': '110px',
              'shape': 'round-rectangle',
              'font-size': '12px',
              'font-weight': 'bold',
              'border-width': '2px',
              'border-color': 'data(borderColor)',
              'border-style': (element: any) => element.data('borderStyle') || 'solid',
              'opacity': (element: any) => element.data('nodeOpacity') ?? 1,
              'padding': '8px',
              'text-wrap': 'wrap',
              'text-max-width': '170px',
            },
          },
          {
            // Selected node: amber border + semi-transparent tint overlay drawn
            // OVER the photo so it is visible for all genders and photo states.
            // overlay-* renders on top of all other node visual layers.
            selector: 'node:selected',
            style: {
              'border-width': '3px',
              'border-color': '#f59e0b',
              'overlay-color': '#f59e0b',
              'overlay-opacity': 0.22,
              'overlay-padding': '4px',
              'opacity': 1,
            },
          },
          {
            selector: 'node[isDeceased = 1]',
            style: {
              'underlay-color': '#E8D8CD',
              'underlay-opacity': 0.38,
              'underlay-padding': '6px',
            },
          },
          {
            selector: 'node[relationshipDropState = "active"]',
            style: {
              'border-width': '4px',
              'border-color': '#7c3aed',
              'overlay-color': '#7c3aed',
              'overlay-opacity': 0.12,
              'overlay-padding': '10px',
            },
          },
          {
            selector: 'node[searchHighlight = "match"]',
            style: {
              'border-width': '3px',
              'border-color': '#f59e0b',
              'overlay-color': '#fef3c7',
              'overlay-opacity': 0.35,
              'overlay-padding': '6px',
            },
          },
          {
            selector: 'node[searchHighlight = "dim"]',
            style: {
              'opacity': 0.25,
            },
          },
          {
            selector: 'edge',
            style: {
              'width': isMobile ? 2.75 : 2.25,
              'line-color': '#9ca3af',
              'source-arrow-color': '#9ca3af',
              'target-arrow-color': '#9ca3af',
              'source-arrow-shape': (element: any) => element.data('sourceArrowShape') || 'none',
              'target-arrow-shape': (element: any) => element.data('targetArrowShape') || 'triangle',
              'line-style': (element: any) => element.data('lineStyle') || 'solid',
              'curve-style': 'bezier',
              'label': 'data(label)',
              'font-size': '10px',
              'text-background-color': '#ffffff',
              'text-background-opacity': 1,
              'text-background-padding': '2px',
            },
          },
        ],
        layout: {
          name: 'preset',
        },
        minZoom: CANVAS_INTERACTION.minZoom,
        maxZoom: CANVAS_INTERACTION.maxZoom,
        // selectionType:'additive' lets .select() accumulate without auto-clearing others.
        selectionType: 'additive',
        // Box selection starts disabled; enabled only while Ctrl is held.
        boxSelectionEnabled: false,
      });

      cy.autoungrabify(!tree.canMoveNodes);

      // Multi-select: sync React state whenever Cytoscape's selection changes
      const syncSelection = () => {
        setSelectedNodeIds(new Set(cy.nodes(':selected').map((n) => n.id())));
      };
      cy.on('select', 'node', syncSelection);
      cy.on('unselect', 'node', syncSelection);

      // Capture selection state on mousedown — BEFORE Cytoscape mutates it.
      // With selectionType:'additive', Cytoscape auto-selects the node before
      // the tap event fires, so we can't tell inside tap whether the node was
      // already selected or just became selected.  mousedown fires first.
      const preClickSelectedIds = new Set<string>();
      cy.on('mousedown', 'node', () => {
        cy.nodes(':selected').forEach((n) => { preClickSelectedIds.add(n.id()); });
      });
      cy.on('mouseup', 'node', () => {
        // Clear after the tap event has been processed (next microtask)
        setTimeout(() => preClickSelectedIds.clear(), 0);
      });

      // Node selection handler
      cy.on('tap', 'node', (evt) => {
        closeContextMenu();
        const nodeId = evt.target.id();
        const currentTree = treeRef.current;
        if (!currentTree) return;

        // Ctrl/Meta + click: toggle this node in/out of selection.
        // If it was already selected before the click → unselect it.
        // If it was NOT selected, Cytoscape's additive mode already added it → keep it.
        if (isCtrlHeldRef.current) {
          if (preClickSelectedIds.has(nodeId)) {
            evt.target.unselect();
          }
          return;
        }

        // Regular click: deselect all other nodes first so this feels like
        // normal single-select (selectionType:'additive' doesn't do this automatically).
        cy.nodes(':selected').not(evt.target).unselect();

        const person = currentTree.people.find((p) => p.id === nodeId);
        if (person) {
          // Lazy load photo when node is clicked
          const photoCacheKey = buildPhotoCacheKey(
            person.photoUrl,
            person.photoCropX,
            person.photoCropY,
            person.photoCropWidth,
            person.photoCropHeight,
            person.photoCropZoom
          );
          if (person.photoUrl && !photoDataUrlsRef.current[photoCacheKey]) {
            loadPhotoLazy(
              person.photoUrl,
              person.id,
              person.photoCropX,
              person.photoCropY,
              person.photoCropWidth,
              person.photoCropHeight,
              person.photoCropZoom
            );
          }
          openPersonPanel(person);

          const now = Date.now();
          const { isDoubleTap, nextLastTappedNode } = evaluateNodeTap(
            lastTappedNodeRef.current,
            nodeId,
            now,
            CANVAS_INTERACTION.nodeDoubleTapThresholdMs
          );

          lastTappedNodeRef.current = nextLastTappedNode;

          if (isDoubleTap && person.canEdit) {
            setShowEditPerson(true);
          }
        } else {
        }
      });

      // Deselect on canvas tap (only when no Ctrl — Ctrl+drag is box select)
      cy.on('tap', (evt) => {
        closeContextMenu();
        if (evt.target === cy) {
          lastTappedNodeRef.current = null;
          setSelectedPerson(null);
          // Reset stuck Ctrl state (e.g. key released while window was blurred)
          isCtrlHeldRef.current = false;
          cy.boxSelectionEnabled(false);
          // Clear multi-selection when tapping empty canvas
          cy.nodes(':selected').unselect();

          const now = Date.now();
          const { isDoubleTap, nextState } = evaluateCanvasDoubleTap(
            lastCanvasTapRef.current,
            now,
            CANVAS_INTERACTION.canvasDoubleTapThresholdMs
          );
          lastCanvasTapRef.current = nextState;

          if (isDoubleTap) {
            const newZoom = computeDoubleTapZoom(
              cy.zoom(),
              CANVAS_INTERACTION.zoomStepMultiplier,
              CANVAS_INTERACTION.maxZoom
            );
            cy.animate({
              zoom: { level: newZoom, position: evt.position },
              duration: CANVAS_INTERACTION.animateDurationMs,
            });
          }
        }
      });

      cy.on('dragfree', 'node', (evt) => {
        if (!treeRef.current?.canMoveNodes) {
          return;
        }

        const draggedNode = evt.target;
        const selectedNodes = cy.nodes(':selected');
        const isGroupMove = selectedNodes.length > 1 && draggedNode.selected();

        const snapNode = (node: ReturnType<typeof cy.nodes>[0]) => {
          const p = node.position();
          const cornerX = p.x - NODE_WIDTH  / 2;
          const cornerY = p.y - NODE_HEIGHT / 2;
          const snappedCornerX = Math.round(cornerX / GRID_MINOR_PX) * GRID_MINOR_PX;
          const snappedCornerY = Math.round(cornerY / GRID_MINOR_PX) * GRID_MINOR_PX;
          node.position({
            x: snappedCornerX + NODE_WIDTH  / 2 + 9,
            y: snappedCornerY + NODE_HEIGHT / 2 + 9,
          });
        };

        if (snapGridRef.current) {
          if (isGroupMove) {
            selectedNodes.forEach((n) => snapNode(n));
          } else {
            snapNode(draggedNode);
          }
        }

        if (isGroupMove) {
          // Batch-save all moved nodes in one API call
          const positions = selectedNodes.map((n) => ({
            personId: n.id(),
            x: n.position().x,
            y: n.position().y,
          }));
          void familyTreesAPI.saveLayout(treeId!, { positions });
        } else {
          const pos = draggedNode.position();
          saveNodePosition(draggedNode.id(), pos.x, pos.y);
        }
      });

      // Handle edge (relationship) clicks
      cy.on('tap', 'edge', (evt) => {
        closeContextMenu();
        lastTappedNodeRef.current = null;
        const edgeId = evt.target.data('id');
        const relationship = tree?.relationships?.find((r) => r.id === edgeId);
        if (relationship) {
          setSelectedRelationship(relationship);
          setShowEditRelationshipModal(true);
        }
      });

      // Load photos for visible nodes when viewport changes
      const loadVisiblePhotos = () => {
        // Get the current viewport extent
        const extent = cy.extent();

        // Check all nodes and load photos for those in or near the viewport
        cy.nodes().forEach((node: any) => {
          const pos = node.position();
          const originalPhotoUrl = node.data('originalPhotoUrl');

          // Simple bounds check: if node position is near the viewport, load its photo
          if (
            originalPhotoUrl &&
            !photoDataUrlsRef.current[buildPhotoCacheKey(
              originalPhotoUrl,
              node.data('photoCropX'),
              node.data('photoCropY'),
              node.data('photoCropWidth'),
              node.data('photoCropHeight'),
              node.data('photoCropZoom')
            )] &&
            !pendingPhotoLoadsRef.current.has(buildPhotoCacheKey(
              originalPhotoUrl,
              node.data('photoCropX'),
              node.data('photoCropY'),
              node.data('photoCropWidth'),
              node.data('photoCropHeight'),
              node.data('photoCropZoom')
            )) &&
            pos.x >= extent.x1 - 200 && pos.x <= extent.x2 + 200 &&
            pos.y >= extent.y1 - 200 && pos.y <= extent.y2 + 200
          ) {
            const personId = node.id();
            const cropX = node.data('photoCropX');
            const cropY = node.data('photoCropY');
            const cropWidth = node.data('photoCropWidth');
            const cropHeight = node.data('photoCropHeight');
            const cropZoom = node.data('photoCropZoom');
            loadPhotoLazy(originalPhotoUrl, personId, cropX, cropY, cropWidth, cropHeight, cropZoom);
          }
        });
      };

      cy.on('pan zoom', () => {
        setHandleOverlayVersion((current) => current + 1);
        // Debounce viewport updates
        if (viewportUpdateTimeout) clearTimeout(viewportUpdateTimeout);
        viewportUpdateTimeout = setTimeout(loadVisiblePhotos, CANVAS_INTERACTION.visiblePhotoLoadDebounceMs);
      });

      // Dynamic font sizing based on zoom level
      const updateFontSizeForZoom = () => {
        if (zoomFontUpdateFrame !== null) {
          cancelAnimationFrame(zoomFontUpdateFrame);
        }

        zoomFontUpdateFrame = requestAnimationFrame(() => {
          zoomFontUpdateFrame = null;

          const zoomLevel = cy.zoom();
          const fontSize = Math.round(
            Math.max(
              CANVAS_INTERACTION.fontSize.minPx,
              Math.min(CANVAS_INTERACTION.fontSize.maxPx, CANVAS_INTERACTION.fontSize.basePx * zoomLevel)
            )
          );

          if (zoomFontSizeRef.current === fontSize) {
            return;
          }

          zoomFontSizeRef.current = fontSize;
          cy.batch(() => {
            cy.nodes().style('font-size', `${fontSize}px`);
          });
        });
      };

      // Listen to zoom change
      cy.on('zoom', updateFontSizeForZoom);
      updateFontSizeForZoom();
      cy.on('position', 'node', () => {
        setHandleOverlayVersion((current) => current + 1);
      });

      const openContextMenu = (
        renderedX: number,
        renderedY: number,
        canvasX: number,
        canvasY: number,
        targetPersonId: string | null
      ) => {
        const availableActions = getContextMenuActions(targetPersonId, treeRef.current);
        if (availableActions.length === 0) {
          closeContextMenu();
          return;
        }

        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds) {
          return;
        }

        const menuWidth = CANVAS_INTERACTION.contextMenu.widthPx;
        const menuHeight = availableActions.length === 1
          ? CANVAS_INTERACTION.contextMenu.emptyHeightPx
          : CANVAS_INTERACTION.contextMenu.emptyHeightPx
            + ((availableActions.length - 1) * (CANVAS_INTERACTION.contextMenu.personHeightPx - CANVAS_INTERACTION.contextMenu.emptyHeightPx));
        const padding = CANVAS_INTERACTION.contextMenu.viewportPaddingPx;

        const clampedX = Math.min(
          Math.max(renderedX, padding),
          Math.max(bounds.width - menuWidth - padding, padding)
        );
        const clampedY = Math.min(
          Math.max(renderedY, padding),
          Math.max(bounds.height - menuHeight - padding, padding)
        );

        setContextMenu({
          isOpen: true,
          screenX: clampedX,
          screenY: clampedY,
          canvasX,
          canvasY,
          targetPersonId,
        });
      };

      cy.on('cxttap', (evt) => {
        lastTappedNodeRef.current = null;
        const renderedPosition = evt.renderedPosition;
        const modelPosition = evt.position;
        const target = evt.target;

        if (!renderedPosition || !modelPosition) {
          return;
        }

        if (target === cy) {
          setSelectedPerson(null);
          openContextMenu(
            renderedPosition.x,
            renderedPosition.y,
            modelPosition.x,
            modelPosition.y,
            null
          );
          return;
        }

        if (target?.isNode?.()) {
          const nodeId = target.id();
          const currentTree = treeRef.current;
          const person = currentTree?.people.find((item) => item.id === nodeId);
          if (person) {
            openPersonPanel(person);
          }

          openContextMenu(
            renderedPosition.x,
            renderedPosition.y,
            modelPosition.x,
            modelPosition.y,
            nodeId
          );
        }
      });

      cyRef.current = cy;

      containerElement = containerRef.current;
      handleNativeContextMenu = (event: MouseEvent) => {
        event.preventDefault();
      };

      containerElement?.addEventListener('contextmenu', handleNativeContextMenu);


      // Load saved viewport after Cytoscape is initialized
      // We need to do this in a microtask to ensure cyRef.current is set
      initialViewportLoadTimeout = setTimeout(() => {
        loadCanvasViewport();
        initialViewportLoadTimeout = null;
      }, 0);

      // Add viewport save listeners (debounced)
      cy.on('zoom', () => {
        saveCanvasViewport();
        if (showGridRef.current && containerRef.current) {
          updateGridBackground(containerRef.current, true, cy.zoom(), cy.pan());
        }
      });
      cy.on('pan', () => {
        saveCanvasViewport();
        if (showGridRef.current && containerRef.current) {
          updateGridBackground(containerRef.current, true, cy.zoom(), cy.pan());
        }
      });
    });

    return () => {
      cancelAnimationFrame(frameId);
      if (zoomFontUpdateFrame !== null) {
        cancelAnimationFrame(zoomFontUpdateFrame);
      }
      if (viewportUpdateTimeout) {
        clearTimeout(viewportUpdateTimeout);
      }
      if (initialViewportLoadTimeout) {
        clearTimeout(initialViewportLoadTimeout);
      }
      if (viewportSaveTimeoutRef.current) {
        clearTimeout(viewportSaveTimeoutRef.current);
      }
      if (containerElement && handleNativeContextMenu) {
        containerElement.removeEventListener('contextmenu', handleNativeContextMenu);
      }
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [closeContextMenu, getCanvasEdgeSemantics, getContextMenuActions, isMobile, loadCanvasViewport, loadPhotoLazy, openPersonPanel, saveCanvasViewport, saveNodePosition, tree, treeId]);

  useEffect(() => {
    if (!relationshipDraft || !containerRef.current) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds) {
        return;
      }

      const renderedX = event.clientX - bounds.left;
      const renderedY = event.clientY - bounds.top;
      const targetNode = findNodeAtRenderedPoint(renderedX, renderedY, relationshipDraft.sourcePersonId);
      setHoveredRelationshipTargetId(targetNode?.id() || null);

      setRelationshipDraft((currentDraft) => {
        if (!currentDraft) {
          return currentDraft;
        }

        return {
          ...currentDraft,
          currentX: renderedX,
          currentY: renderedY,
        };
      });
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (!treeRef.current?.canAddRelationship) {
        setRelationshipDraft(null);
        setHoveredRelationshipTargetId(null);
        return;
      }

      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds) {
        setRelationshipDraft(null);
        return;
      }

      const renderedX = event.clientX - bounds.left;
      const renderedY = event.clientY - bounds.top;
      const targetNode = findNodeAtRenderedPoint(renderedX, renderedY, relationshipDraft.sourcePersonId);

      setRelationshipDraft(null);
      setHoveredRelationshipTargetId(null);

      if (!targetNode) {
        return;
      }

      const dx = renderedX - relationshipDraft.startX;
      const dy = renderedY - relationshipDraft.startY;
      const dragAngle = computeDragAngleDeg(dx, dy);
      const inferredType = inferRelationshipTypeFromAngle(dragAngle);

      setPendingRelationshipPeople({
        ...buildRightClickRelationshipDraft(relationshipDraft.sourcePersonId),
        personBId: targetNode.id(),
      });
      setPendingRelationshipType(inferredType);
      setShowAddRelationship(true);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [relationshipDraft, findNodeAtRenderedPoint]);

  useEffect(() => {
    if (!cyRef.current) {
      return;
    }

    cyRef.current.batch(() => {
      cyRef.current?.nodes().forEach((node: any) => {
        node.data('relationshipDropState', node.id() === hoveredRelationshipTargetId ? 'active' : '');
      });
    });
  }, [hoveredRelationshipTargetId]);

  // Apply search highlight data to nodes and auto-focus once when a search collapses to a single match.
  useEffect(() => {
    if (!cyRef.current) return;

    const isSearchActive = activeSearchQuery.trim().length > 0;
    const searchSignature = `${activeSearchQuery.trim().toLowerCase()}|${activeSearchResults.map((person) => person.id).join(',')}`;

    cyRef.current.batch(() => {
      cyRef.current?.nodes().forEach((node: any) => {
        if (!isSearchActive) {
          node.data('searchHighlight', '');
        } else {
          node.data('searchHighlight', activeSearchIds.has(node.id()) ? 'match' : 'dim');
        }
      });
    });

    if (!isSearchActive) {
      lastAutoFocusedSearchSignatureRef.current = '';
      return;
    }

    if (activeSearchResults.length === 1 && lastAutoFocusedSearchSignatureRef.current !== searchSignature) {
      lastAutoFocusedSearchSignatureRef.current = searchSignature;
      focusSearchResult(activeSearchResults[0]);
      return;
    }

    if (activeSearchResults.length > 1 && lastAutoFocusedSearchSignatureRef.current !== searchSignature) {
      lastAutoFocusedSearchSignatureRef.current = searchSignature;
      setSearchResultIndex(0);
    }
  }, [activeSearchIds, activeSearchQuery, activeSearchResults, focusSearchResult]);

  // SEPARATE EFFECT: Update Cytoscape nodes when photos load, WITHOUT destroying the instance
  useEffect(() => {

    if (!cyRef.current) {
      return;
    }

    const cy = cyRef.current;
    const updatedPhotoUrls = Object.entries(photoDataUrls).filter(([photoCacheKey, dataUrl]) => {
      return appliedPhotoDataUrlsRef.current[photoCacheKey] !== dataUrl;
    });

    if (updatedPhotoUrls.length === 0) {
      return;
    }

    const updatedPhotoUrlMap = new Map(updatedPhotoUrls);

    cy.batch(() => {
      cy.nodes().forEach((node: any) => {
        const photoCacheKey = node.data('photoCacheKey');
        if (!photoCacheKey) {
          return;
        }

        const updatedPhotoUrl = updatedPhotoUrlMap.get(photoCacheKey);
        if (updatedPhotoUrl && node.data('photoUrl') !== updatedPhotoUrl) {
          node.data('photoUrl', updatedPhotoUrl);
        }
      });
    });

    updatedPhotoUrls.forEach(([photoCacheKey, dataUrl]) => {
      appliedPhotoDataUrlsRef.current[photoCacheKey] = dataUrl;
    });
  }, [photoDataUrls]);

  const fitCanvas = async () => {
    if (!cyRef.current) return;
    try {
      cyRef.current.animate(
        { fit: { eles: cyRef.current.elements(), padding: CANVAS_INTERACTION.fitPaddingPx } },
        { duration: CANVAS_INTERACTION.animateDurationMs },
      );
      // Save after animation settles
      setTimeout(() => {
        if (!cyRef.current || !treeId) return;
        const zoomLevel = Math.min(CANVAS_INTERACTION.maxZoom, Math.max(CANVAS_INTERACTION.minZoom, cyRef.current.zoom()));
        const pan = cyRef.current.pan();
        void familyTreesAPI.saveCanvasViewport(treeId, { zoomLevel, panX: pan.x, panY: pan.y });
      }, CANVAS_INTERACTION.animateDurationMs + 50);
    } catch (_) {}
  };

  const resetZoom = () => {
    if (!cyRef.current) return;
    const elems = cyRef.current.elements();
    if (elems.length === 0) return;
    // Centre the viewport on the bounding box of all nodes at zoom=1
    const bb = elems.boundingBox({});
    const nodeCentreX = (bb.x1 + bb.x2) / 2;
    const nodeCentreY = (bb.y1 + bb.y2) / 2;
    const container = containerRef.current;
    const w = container?.clientWidth ?? 800;
    const h = container?.clientHeight ?? 600;
    cyRef.current.animate(
      { zoom: 1, pan: { x: w / 2 - nodeCentreX, y: h / 2 - nodeCentreY } },
      { duration: CANVAS_INTERACTION.animateDurationMs },
    );
    if (treeId) {
      setTimeout(() => {
        if (!cyRef.current || !treeId) return;
        const pan = cyRef.current.pan();
        void familyTreesAPI.saveCanvasViewport(treeId, { zoomLevel: 1, panX: pan.x, panY: pan.y });
      }, CANVAS_INTERACTION.animateDurationMs + 50);
    }
  };

  const zoomIn = () => {
    if (!cyRef.current) {
      return;
    }

    const currentZoom = cyRef.current.zoom();
    const newZoom = Math.min(
      currentZoom * CANVAS_INTERACTION.zoomStepMultiplier,
      CANVAS_INTERACTION.maxZoom
    );

    cyRef.current.animate({
      zoom: newZoom,
      duration: CANVAS_INTERACTION.animateDurationMs,
    });

    saveCanvasViewport();
  };

  const zoomOut = () => {
    if (!cyRef.current) {
      return;
    }

    const currentZoom = cyRef.current.zoom();
    const newZoom = Math.max(
      currentZoom / CANVAS_INTERACTION.zoomStepMultiplier,
      CANVAS_INTERACTION.minZoom
    );

    cyRef.current.animate({
      zoom: newZoom,
      duration: CANVAS_INTERACTION.animateDurationMs,
    });

    saveCanvasViewport();
  };

  const handleSaveTreeName = async (newName: string) => {
    if (!treeId || !tree || !tree.canEdit) return;

    try {
      setIsSavingName(true);
      await familyTreesAPI.updateTree(treeId, { name: newName, description: tree.description });
      setTree({ ...tree, name: newName });
    } catch (err) {
      throw err;
    } finally {
      setIsSavingName(false);
    }
  };

  const handleSaveTreeDescription = async (newDescription: string) => {
    if (!treeId || !tree || !tree.canEdit) return;

    try {
      setIsSavingDescription(true);
      await familyTreesAPI.updateTree(treeId, { name: tree.name, description: newDescription });
      // Update only the description ref so tree state (and Cytoscape) are NOT touched
      setTreeDescription(newDescription);
    } catch (err) {
      throw err;
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleDeletePerson = async (personId: string) => {
    if (!treeId) return;

    const currentPerson = treeRef.current?.people.find((person) => person.id === personId);
    if (!currentPerson?.canDelete) return;

    try {
      await peopleAPI.deletePerson(personId);
      setSelectedPerson(null);
      // Reload tree to get updated data
      const response = await familyTreesAPI.getTree(treeId);
      if (response.data.statusCode === 200) {
        setTree(response.data.data);
      }
    } catch (err) {
      alert('Failed to delete person');
    }
  };

  const handleBulkDelete = async () => {
    if (!treeId) return;
    const ids = Array.from(selectedNodeIds);
    try {
      for (const id of ids) {
        const person = treeRef.current?.people.find((p) => p.id === id);
        if (person?.canDelete) {
          await peopleAPI.deletePerson(id);
        }
      }
      cyRef.current?.nodes(':selected').unselect();
      setSelectedNodeIds(new Set());
      setSelectedPerson(null);
      setShowBulkDeleteConfirm(false);
      const response = await familyTreesAPI.getTree(treeId);
      if (response.data.statusCode === 200) {
        setTree(response.data.data);
      }
    } catch (err) {
      alert('Failed to delete selected persons');
    }
  };

  const getVisibleCanvasCenter = () => {
    if (!cyRef.current) {
      return null;
    }

    const extent = cyRef.current.extent();
    return {
      x: (extent.x1 + extent.x2) / 2,
      y: (extent.y1 + extent.y2) / 2,
    };
  };

  if (isLoading) {
    return (
      <MainLayout fullBleed showFooter={false}>
        <div className="flex flex-1 items-center justify-center">
          <p className={`text-lg ${brandClasses.mutedText}`}>Loading family tree...</p>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout fullBleed showFooter={false}>
        <div className={`flex flex-1 items-center justify-center ${brandClasses.appShell}`}>
          <div className="text-center">
            <p className="text-lg text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/trees')}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition ${brandClasses.actionButton}`}
              style={{ borderColor: '#7f8d57' }}
            >
              Back to Trees
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout fullBleed showFooter={false}>
    <div className={`flex-1 flex min-h-0 flex-col ${brandClasses.warmSurface}`} data-testid="canvas-page">
      {/* Editable Tree Title */}
      {tree && (
        <EditableTreeTitle
          currentName={tree.name}
          onSave={handleSaveTreeName}
          readonly={!tree.canEdit || isSavingName}
          currentDescription={treeDescription}
          onSaveDescription={tree.canEdit ? handleSaveTreeDescription : undefined}
          readonlyDescription={!tree.canEdit || isSavingDescription}
        />
      )}

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        <div
          className="flex-1 relative"
          style={{ minWidth: 0 }}
          onContextMenu={(event) => {
            event.preventDefault();
          }}
          data-testid="canvas-root"
        >
          {/* Cytoscape container */}
          <div ref={containerRef} className="absolute inset-0" style={{ height: '100%' }} />

          {/* Multi-select action bar — appears when 2+ nodes are selected */}
          {selectedNodeIds.size >= 2 && (() => {
            const selectedPeople = Array.from(selectedNodeIds).map((id) =>
              treeRef.current?.people.find((p) => p.id === id)
            ).filter(Boolean);
            const canDeleteAll = selectedPeople.every((p) => p?.canDelete);
            return (
              <div
                data-testid="multi-select-bar"
                className="pointer-events-auto absolute top-14 left-1/2 z-30 -translate-x-1/2 flex items-center gap-3 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-2.5 shadow-[0_6px_24px_rgba(95,83,58,0.18)]"
              >
                <span className="text-sm font-semibold text-[#3D3530]">
                  {selectedNodeIds.size} selected
                </span>
                <button
                  onClick={() => cyRef.current?.nodes(':selected').unselect()}
                  className="rounded-lg px-3 py-1 text-xs font-medium text-[#6B6560] hover:bg-[#EFEAE3] transition-colors"
                >
                  Deselect all
                </button>
                {canDeleteAll && tree?.canEdit && (
                  showBulkDeleteConfirm ? (
                    <>
                      <span className="text-xs font-medium text-[#8C2F2F]">Delete {selectedNodeIds.size} persons?</span>
                      <button
                        data-testid="bulk-delete-confirm"
                        onClick={() => void handleBulkDelete()}
                        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setShowBulkDeleteConfirm(false)}
                        className="rounded-lg px-3 py-1 text-xs font-medium text-[#6B6560] hover:bg-[#EFEAE3] transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      data-testid="bulk-delete-button"
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      className="rounded-lg bg-red-50 px-3 py-1 text-xs font-medium text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                    >
                      Delete {selectedNodeIds.size}
                    </button>
                  )
                )}
              </div>
            );
          })()}
          {!isBlankCanvas && !isMobile && (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-30 flex justify-center px-4">
              <div className="pointer-events-auto flex items-center gap-2 rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2 shadow-[0_4px_16px_rgba(95,83,58,0.14)]">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 text-[#8D867E]"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="search"
                  data-testid="canvas-search-input"
                  placeholder={t('toolbar.findPersonPlaceholder', { ns: 'canvas' })}
                  aria-label={t('toolbar.findPerson', { ns: 'canvas' })}
                  value={searchQuery}
                  onChange={(e) => {
                    clearAiSearch();
                    setSearchQuery(e.target.value);
                  }}
                  className="w-44 bg-transparent text-sm text-[#1A1714] outline-none placeholder:text-[#8D867E]"
                />
                {activeSearchQuery.trim() && (
                  <>
                    <span
                      data-testid="canvas-search-count"
                      className={`shrink-0 text-xs font-medium ${activeSearchCount === 0 ? 'text-[#A32D2D]' : 'text-[#4f5d2f]'}`}
                    >
                      {getSearchSummary(activeSearchCount, activeSearchQuery)}
                    </span>
                    {activeSearchCount > 1 && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid="canvas-search-prev"
                          aria-label={t('toolbar.prevResult', { ns: 'canvas' })}
                          onClick={() => stepSearchResult(-1)}
                          className="rounded-lg p-1 text-[#7C766F] transition hover:bg-[#EFEAE3] hover:text-[#1A1714]"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" />
                          </svg>
                        </button>
                        <span className="min-w-12 text-center text-[11px] text-[#6B6560]">
                          {searchResultIndex + 1}/{activeSearchCount}
                        </span>
                        <button
                          type="button"
                          data-testid="canvas-search-next"
                          aria-label={t('toolbar.nextResult', { ns: 'canvas' })}
                          onClick={() => stepSearchResult(1)}
                          className="rounded-lg p-1 text-[#7C766F] transition hover:bg-[#EFEAE3] hover:text-[#1A1714]"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9 6l6 6-6 6" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      data-testid="canvas-search-clear"
                      aria-label={t('toolbar.clearSearch', { ns: 'canvas' })}
                      onClick={() => {
                        clearAiSearch();
                        setSearchQuery('');
                      }}
                      className="shrink-0 rounded-lg p-1 text-[#7C766F] transition hover:bg-[#EFEAE3] hover:text-[#1A1714]"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {selectedPerson && canCreateRelationships && getSelectedNodeHandlePoints().length > 0 && (
            <div className="absolute inset-0 z-20 pointer-events-none">
              {getSelectedNodeHandlePoints().map((handle) => (
                <button
                  key={handle.key}
                  type="button"
                  className="absolute rounded-full border-2 border-[#8fa36b] bg-[#fffaf2] shadow-sm pointer-events-auto -translate-x-1/2 -translate-y-1/2 transition-transform hover:scale-110"
                  style={{
                    left: handle.x,
                    top: handle.y,
                    width: CANVAS_INTERACTION.relationshipHandleSizePx,
                    height: CANVAS_INTERACTION.relationshipHandleSizePx,
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    beginRelationshipDraft(selectedPerson.id, handle.x, handle.y);
                  }}
                  title="Drag to create relationship"
                />
              ))}

              {relationshipDraft && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
                  <defs>
                    <marker
                      id="relationship-draft-arrow"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="#4f46e5" />
                    </marker>
                  </defs>
                  <line
                    x1={relationshipDraft.startX}
                    y1={relationshipDraft.startY}
                    x2={relationshipDraft.currentX}
                    y2={relationshipDraft.currentY}
                    stroke="#4f46e5"
                    strokeWidth="2.5"
                    strokeDasharray="6 4"
                    markerEnd="url(#relationship-draft-arrow)"
                  />
                </svg>
              )}
            </div>
          )}

          {/* Blank canvas prompt - separate overlay */}
          {isBlankCanvas && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-b from-transparent to-[#F7F4EB] pointer-events-none">
              <div className="text-center pointer-events-auto">
                <p className="mb-6 text-2xl font-semibold text-[#4a453b]">Start Building Your Family Tree</p>
                {canAddPeople && (
                  <button
                    onClick={() => {
                      setPendingAddPersonPosition(null);
                      setShowAddPerson(true);
                    }}
                    data-testid="blank-canvas-add-person-button"
                    className="rounded-lg bg-[#8fa36b] px-8 py-4 text-lg font-medium text-white shadow-lg transition-all hover:bg-[#7f8d57] hover:shadow-xl"
                  >
                    + Add First Person
                  </button>
                )}
              </div>
            </div>
          )}

          {contextMenu.isOpen && (
            <div
              ref={contextMenuRef}
              role="menu"
              aria-label={contextMenu.targetPersonId ? 'Person actions' : 'Canvas actions'}
              tabIndex={-1}
              className={`absolute z-40 min-w-[220px] rounded-xl border py-2 shadow-2xl ${brandClasses.strongBorder} ${brandClasses.warmSurface}`}
              style={{
                left: contextMenu.screenX,
                top: contextMenu.screenY,
              }}
              onKeyDown={(event) => {
                if (!contextMenuRef.current) return;
                const items = Array.from(
                  contextMenuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
                );
                const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
                const result = evaluateMenuKeyNavigation(event.key, currentIndex, items.length);
                if (result.preventDefault) event.preventDefault();
                if (result.closeMenu) closeContextMenu();
                if (result.nextIndex !== null) items[result.nextIndex]?.focus();
              }}
            >
              {getContextMenuActions(contextMenu.targetPersonId).includes(ADD_PERSON_ACTION) && (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={`flex w-full items-center px-4 py-3 text-left text-sm font-medium transition ${brandClasses.primaryText} ${brandClasses.hoverSurface}`}
                  onClick={() => {
                    setPendingAddPersonPosition({
                      x: contextMenu.canvasX,
                      y: contextMenu.canvasY,
                    });
                    setShowAddPerson(true);
                    closeContextMenu();
                  }}
                >
                  {ADD_PERSON_ACTION}
                </button>
              )}

              {getContextMenuActions(contextMenu.targetPersonId).includes(ADD_RELATION_ACTION) && (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={`flex w-full items-center px-4 py-3 text-left text-sm font-medium transition ${brandClasses.primaryText} ${brandClasses.hoverSurface}`}
                  onClick={() => {
                    setPendingRelationshipPeople(buildRightClickRelationshipDraft(contextMenu.targetPersonId!));
                    setPendingRelationshipType(null);
                    setShowAddRelationship(true);
                    closeContextMenu();
                  }}
                >
                  {ADD_RELATION_ACTION}
                </button>
              )}

              {getContextMenuActions(contextMenu.targetPersonId).includes(EDIT_PERSON_ACTION) && (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  data-testid="context-menu-edit-person"
                  className={`flex w-full items-center px-4 py-3 text-left text-sm font-medium transition ${brandClasses.primaryText} ${brandClasses.hoverSurface}`}
                  onClick={() => {
                    if (!contextMenu.targetPersonId) {
                      closeContextMenu();
                      return;
                    }

                    const targetPerson = tree?.people.find((person) => person.id === contextMenu.targetPersonId);
                    if (targetPerson?.canEdit) {
                      openPersonPanel(targetPerson);
                      setShowEditPerson(true);
                    }

                    closeContextMenu();
                  }}
                >
                  {EDIT_PERSON_ACTION}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right sidebar */}
        {isDesktopSidebarVisible && (
          <div className={`w-96 flex-shrink-0 overflow-y-auto border-l ${brandClasses.subtleBorder} ${brandClasses.appShell}`} style={{ minHeight: 0 }}>
            {isAiEnabled && showAiPanel ? (
              <AiCommandPanel
                treeId={treeId!}
                treeName={tree?.name || 'Current Tree'}
                currentUserId={user?.id}
                onClose={() => setShowAiPanel(false)}
                onOpenPerson={openPersonPanel}
                onSearchResults={setSearchResultsFromAi}
                onFocusPerson={(personId) => {
                  const person = tree?.people.find((item) => item.id === personId);
                  if (person) {
                    openPersonPanel(person);
                  }
                }}
              />
            ) : showMembersPanel ? (
              <MembersPanel
                treeId={treeId!}
                treeName={tree?.name || 'Current Tree'}
                currentUserId={user?.id}
                currentUserRole={deriveCurrentUserTreeRole(user?.id, tree?.ownerId, tree?.members)}
                canManageMembers={canManageMembers}
                canTransferOwnership={Boolean(tree?.canTransferOwnership)}
                onClose={() => setShowMembersPanel(false)}
                onUpdated={refreshCurrentTree}
              />
            ) : !isMobile && selectedPerson ? (
              <PersonDetailsPanel
                person={selectedPerson}
                canAddRelationship={canCreateRelationships}
                onClose={() => setSelectedPerson(null)}
                onEdit={() => setShowEditPerson(true)}
                onAddRelationship={() => {
                  setPendingRelationshipPeople(buildRightClickRelationshipDraft(selectedPerson.id));
                  setPendingRelationshipType(null);
                  setShowAddRelationship(true);
                }}
                onDelete={handleDeletePerson}
              />
            ) : null}
          </div>
        )}
      </div>

      <div data-testid="desktop-canvas-controls" className={`absolute bottom-6 z-20 hidden flex-col gap-3 transition-all duration-200 md:flex ${!isMobile && isRightSidebarVisible ? 'right-[30rem]' : 'right-6'}`}>
        {canAddPeople && (
          <button
            onClick={openAddPersonModal}
            title={isBlankCanvas ? t('toolbar.addPerson', { ns: 'canvas' }) : t('toolbar.addPerson', { ns: 'canvas' })}
            data-testid="add-person-button"
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#bfd7f2] to-[#97bee7] text-white shadow-[0_12px_28px_rgba(151,190,231,0.35)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(151,190,231,0.45)] flex items-center justify-center transition-all"
          >
            <ActionPaletteIcon kind="addPerson" />
          </button>
        )}

        {!isBlankCanvas && canCreateRelationships && (
          <button
            onClick={openAddRelationshipModal}
            title={t('toolbar.addRelationship', { ns: 'canvas' })}
            data-testid="add-relationship-button"
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#c7ddb8] to-[#a9cea0] text-white shadow-[0_12px_28px_rgba(169,206,160,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(169,206,160,0.44)] flex items-center justify-center transition-all"
          >
            <ActionPaletteIcon kind="addRelationship" />
          </button>
        )}

        {!isBlankCanvas && (
          <div className="flex flex-col gap-3">
            <button
              onClick={zoomIn}
              title={t('toolbar.zoomIn', { ns: 'canvas' })}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#a8d7d8] to-[#84c5c6] text-white shadow-[0_12px_28px_rgba(132,197,198,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(132,197,198,0.44)] flex items-center justify-center transition-all"
            >
              <ActionPaletteIcon kind="zoomIn" />
            </button>
            <button
              onClick={() => void fitCanvas()}
              title={t('toolbar.fitAll', { ns: 'canvas' })}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#c7ddb8] to-[#a9cea0] text-white shadow-[0_12px_28px_rgba(169,206,160,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(169,206,160,0.44)] flex items-center justify-center transition-all"
            >
              <ActionPaletteIcon kind="fit" />
            </button>
            <button
              onClick={resetZoom}
              title={t('toolbar.resetZoom', { ns: 'canvas' })}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#f4bf9f] to-[#eca57f] text-white shadow-[0_12px_28px_rgba(236,165,127,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(236,165,127,0.44)] flex items-center justify-center transition-all"
            >
              <ActionPaletteIcon kind="reset" />
            </button>
            <button
              onClick={zoomOut}
              title={t('toolbar.zoomOut', { ns: 'canvas' })}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#a8d7d8] to-[#84c5c6] text-white shadow-[0_12px_28px_rgba(132,197,198,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(132,197,198,0.44)] flex items-center justify-center transition-all"
            >
              <ActionPaletteIcon kind="zoomOut" />
            </button>
            <button
              onClick={() => setShowGrid((prev) => !prev)}
              title={showGrid ? t('toolbar.hideGrid', { ns: 'canvas' }) : t('toolbar.showGrid', { ns: 'canvas' })}
              className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                showGrid
                  ? 'bg-gradient-to-br from-[#c8b8e8] to-[#a990d4] text-white shadow-[0_12px_28px_rgba(169,144,212,0.44)] hover:-translate-y-0.5'
                  : 'bg-gradient-to-br from-[#e7edf4] to-[#d6dee7] text-[#4b5563] shadow-[0_12px_28px_rgba(214,222,231,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(214,222,231,0.44)]'
              }`}
            >
              <ActionPaletteIcon kind="grid" />
            </button>
            <button
              onClick={() => setSnapGrid((prev) => !prev)}
              title={snapGrid ? t('toolbar.snapOn', { ns: 'canvas' }) : t('toolbar.snapOff', { ns: 'canvas' })}
              className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                snapGrid
                  ? 'bg-gradient-to-br from-[#f4bf9f] to-[#eca57f] text-white shadow-[0_12px_28px_rgba(236,165,127,0.44)] hover:-translate-y-0.5'
                  : 'bg-gradient-to-br from-[#e7edf4] to-[#d6dee7] text-[#4b5563] shadow-[0_12px_28px_rgba(214,222,231,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(214,222,231,0.44)]'
              }`}
            >
              <ActionPaletteIcon kind="snap" />
            </button>
          </div>
        )}

        {isAiEnabled && (
          <button
            onClick={openAiPanel}
            title="AI"
            data-testid="ai-panel-button"
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#d8c9f4] to-[#bfa7eb] text-[#5f3d8c] shadow-[0_12px_28px_rgba(191,167,235,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(191,167,235,0.44)] flex items-center justify-center transition-all"
          >
            <ActionPaletteIcon kind="ai" />
          </button>
        )}
        <button
          onClick={openMembersPanel}
          title={t('toolbar.members', { ns: 'canvas' })}
          className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#cbc8ef] to-[#a4a1dd] text-[#6463a8] shadow-[0_12px_28px_rgba(164,161,221,0.3)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(164,161,221,0.4)] flex items-center justify-center transition-all"
        >
          <ActionPaletteIcon kind="members" />
        </button>
        {canManagePublicTree && (
          <button
            onClick={() => navigate(`/tree/${treeId}/public`)}
            title="Public Tree"
            data-testid="public-tree-button"
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#c8e6d4] to-[#9fd3b2] text-[#2d6a4f] shadow-[0_12px_28px_rgba(159,211,178,0.3)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(159,211,178,0.4)] flex items-center justify-center transition-all"
          >
            <ActionPaletteIcon kind="publicTree" />
          </button>
        )}
        <button
          onClick={() => navigate('/trees')}
          title={t('toolbar.backToTrees', { ns: 'canvas' })}
          className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#e7edf4] to-[#d6dee7] text-[#4b5563] shadow-[0_12px_28px_rgba(214,222,231,0.34)] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(214,222,231,0.44)] flex items-center justify-center transition-all"
        >
          <ActionPaletteIcon kind="back" />
        </button>
      </div>

      {/* Mobile bottom sheet — person details on narrow screens (<md).
          Slides up from the bottom when a person is selected on mobile.
          Desktop sidebar handles this on wider screens. */}
      {isMobile && (
        <MobileCanvasHUD
          isBlankCanvas={isBlankCanvas}
          selectedPerson={selectedPerson}
          canAddPeople={canAddPeople}
          canCreateRelationships={canCreateRelationships}
          isAiEnabled={isAiEnabled}
          showGrid={showGrid}
          snapGrid={snapGrid}
          searchQuery={searchQuery}
          activeSearchQuery={activeSearchQuery}
          activeSearchCount={activeSearchCount}
          searchResultIndex={searchResultIndex}
          onSearchChange={(query) => {
            clearAiSearch();
            setSearchQuery(query);
          }}
          onSearchClear={() => {
            clearAiSearch();
            setSearchQuery('');
          }}
          onSearchStep={stepSearchResult}
          onAddPerson={openAddPersonModal}
          onAddRelationship={() => {
            if (!selectedPerson) {
              return;
            }

            setPendingRelationshipPeople(buildRightClickRelationshipDraft(selectedPerson.id));
            setPendingRelationshipType(null);
            setShowAddRelationship(true);
          }}
          onFit={() => void fitCanvas()}
          onOpenMembers={openMembersPanel}
          onOpenAi={openAiPanel}
          onToggleGrid={() => setShowGrid((prev) => !prev)}
          onToggleSnap={() => setSnapGrid((prev) => !prev)}
        />
      )}
      {isMobile && selectedPerson && (
        <>
          {/* Backdrop */}
          <div
            className="absolute inset-0 z-30 bg-black/30"
            onClick={() => setSelectedPerson(null)}
            aria-hidden="true"
          />
          {/* Sheet */}
          <div
            role="dialog"
            aria-label={`${selectedPerson.firstName} ${selectedPerson.lastName} details`}
            aria-modal="true"
            data-testid="person-bottom-sheet"
            className={`absolute bottom-0 left-0 right-0 z-40 max-h-[80vh] overflow-y-auto rounded-t-3xl border-t shadow-2xl ${brandClasses.subtleBorder} ${brandClasses.appShell}`}
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-[#d8cfbd]" />
            </div>
            <PersonDetailsPanel
              person={selectedPerson}
              canAddRelationship={canCreateRelationships}
              onClose={() => setSelectedPerson(null)}
              onEdit={() => setShowEditPerson(true)}
              onAddRelationship={() => {
                setPendingRelationshipPeople(buildRightClickRelationshipDraft(selectedPerson.id));
                setPendingRelationshipType(null);
                setShowAddRelationship(true);
              }}
              onDelete={handleDeletePerson}
            />
          </div>
        </>
      )}

      {/* Node action tray — touch-friendly quick actions for the selected person node.
          Visible whenever a person is selected; supplements the right-click context
          menu for devices where right-click is unavailable. */}
      {!isMobile && selectedPerson && getNodeActionTrayItems({ canAddPerson: canAddPeople, canAddRelationship: canCreateRelationships }).length > 0 && (
        <div
          data-testid="node-action-tray"
          className={`absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-2xl border px-4 py-2 shadow-xl ${brandClasses.strongBorder} ${brandClasses.warmSurface}`}
        >
          {canAddPeople && (
            <button
              type="button"
              data-testid="node-action-tray-add-person"
              title={t('toolbar.addPerson', { ns: 'canvas' })}
              className={`flex min-h-[44px] items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition touch-manipulation ${brandClasses.primaryText} ${brandClasses.hoverSurface}`}
              onClick={openAddPersonModal}
            >
              <ActionPaletteIcon kind="addPerson" />
              <span>{t('toolbar.addPerson', { ns: 'canvas' })}</span>
            </button>
          )}
          {canCreateRelationships && (
            <button
              type="button"
              data-testid="node-action-tray-add-relation"
              title={t('toolbar.addRelation', { ns: 'canvas' })}
              className={`flex min-h-[44px] items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition touch-manipulation ${brandClasses.primaryText} ${brandClasses.hoverSurface}`}
              onClick={() => {
                setPendingRelationshipPeople(buildRightClickRelationshipDraft(selectedPerson.id));
                setPendingRelationshipType(null);
                setShowAddRelationship(true);
              }}
            >
              <ActionPaletteIcon kind="addRelationship" />
              <span>{t('toolbar.addRelation', { ns: 'canvas' })}</span>
            </button>
          )}
        </div>
      )}

      {/* Add Person Modal */}
      {showAddPerson && treeId && canAddPeople && (
        <AddPersonModal
          treeId={treeId}
          showContinueToCompleteProfile={!pendingRelatedPersonSourceId}
          onClose={() => {
            setShowAddPerson(false);
            setPendingAddPersonPosition(null);
            setPendingRelatedPersonSourceId(null);
            if (returnToEditPersonAfterAddPerson) {
              setReturnToEditPersonAfterAddPerson(false);
              reopenEditPersonFromTree();
            }
          }}
          onSuccess={async ({ newPersonId, continueToCompleteProfile }: AddPersonSuccessResult) => {
            setShowAddPerson(false);

            const targetPosition = pendingAddPersonPosition || getVisibleCanvasCenter();
            setPendingAddPersonPosition(null);
            const relationshipSourcePersonId = pendingRelatedPersonSourceId;
            setPendingRelatedPersonSourceId(null);
            const shouldReturnToEditAfterAddPerson = returnToEditPersonAfterAddPerson;
            setReturnToEditPersonAfterAddPerson(false);
            const relationshipTypeToCreate = pendingRelationshipType;
            setPendingRelationshipType(null);

            try {
              const response = await familyTreesAPI.getTree(treeId);
              if (response.data.statusCode !== 200) {
                return;
              }

              const treeData = response.data.data as FamilyTreeDetailedDto;
              let nextTree = treeData;

              if (newPersonId && targetPosition) {
                nextTree = {
                  ...treeData,
                  people: treeData.people.map((person) => {
                    if (person.id !== newPersonId) {
                      return person;
                    }

                    return {
                      ...person,
                      x: targetPosition.x,
                      y: targetPosition.y,
                    };
                  }),
                };

                await familyTreesAPI.saveLayout(treeId, {
                  positions: [{ personId: newPersonId, x: targetPosition.x, y: targetPosition.y }],
                });
              }

              setTree(nextTree);
              treeRef.current = nextTree;

              if (relationshipSourcePersonId && newPersonId && canCreateRelationships) {
                if (relationshipTypeToCreate) {
                  await relationshipsAPI.createRelationship({
                    treeId,
                    personAId: relationshipSourcePersonId,
                    personBId: newPersonId,
                    type: relationshipTypeToCreate,
                  });

                  const refreshedResponse = await familyTreesAPI.getTree(treeId);
                  if (refreshedResponse.data.statusCode === 200) {
                    const refreshedTree = refreshedResponse.data.data;
                    setTree(refreshedTree);
                    treeRef.current = refreshedTree;

                    if (shouldReturnToEditAfterAddPerson) {
                      reopenEditPersonFromTree(refreshedTree);
                    }
                  }
                } else {
                  setReturnToEditPersonAfterAddRelationship(shouldReturnToEditAfterAddPerson);
                  setPendingRelationshipPeople({
                    personAId: relationshipSourcePersonId,
                    personBId: newPersonId,
                  });
                  setShowAddRelationship(true);
                }
                return;
              }

              if (continueToCompleteProfile && newPersonId) {
                const createdPerson = nextTree.people.find((person) => person.id === newPersonId);
                if (createdPerson) {
                  openPersonPanel(createdPerson);
                  setShowEditPerson(true);
                }
                return;
              }

              if (shouldReturnToEditAfterAddPerson) {
                reopenEditPersonFromTree(nextTree);
              }
            } catch (err) {
            }
          }}
        />
      )}

      {/* Add Relationship Modal */}
      {showAddRelationship && treeId && tree && canCreateRelationships && (
        <AddRelationshipModal
          treeId={treeId}
          people={tree.people}
          initialPersonAId={pendingRelationshipPeople?.personAId}
          initialPersonBId={pendingRelationshipPeople?.personBId}
          initialType={pendingRelationshipType ?? undefined}
          lockPeopleSelection={Boolean(pendingRelationshipPeople?.personAId && pendingRelationshipPeople?.personBId)}
          onClose={() => {
            setShowAddRelationship(false);
            setPendingRelationshipPeople(null);
            setPendingRelationshipType(null);
            if (returnToEditPersonAfterAddRelationship) {
              setReturnToEditPersonAfterAddRelationship(false);
              reopenEditPersonFromTree();
            }
          }}
          onSuccess={() => {
            const shouldReturnToEdit = returnToEditPersonAfterAddRelationship;
            setShowAddRelationship(false);
            setPendingRelationshipPeople(null);
            setPendingRelationshipType(null);
            setReturnToEditPersonAfterAddRelationship(false);
            // Reload tree to get the new relationship
            const loadTree = async () => {
              try {
                const response = await familyTreesAPI.getTree(treeId);
                if (response.data.statusCode === 200) {
                  setTree(response.data.data);
                  treeRef.current = response.data.data;
                  if (shouldReturnToEdit) {
                    reopenEditPersonFromTree(response.data.data);
                  }
                }
              } catch (err) {
              }
            };
            loadTree();
          }}
        />
      )}

      {showExistingPersonPicker && selectedPerson && treeId && tree && canCreateRelationships && (
        <ExistingPersonPickerModal
          currentPerson={selectedPerson}
          people={tree.people}
          onClose={() => {
            setShowExistingPersonPicker(false);
            setShowEditPerson(true);
          }}
          onSelect={({ relatedPersonId, relationshipType }) => {
            setShowExistingPersonPicker(false);
            setReturnToEditPersonAfterAddRelationship(true);
            setPendingRelationshipPeople({
              personAId: selectedPerson.id,
              personBId: relatedPersonId,
            });
            setPendingRelationshipType(relationshipType);
            setShowAddRelationship(true);
          }}
        />
      )}

      {/* Edit Person Modal */}
      {showEditPerson && selectedPerson && treeId && selectedPerson.canEdit && (
        <EditPersonModal
          treeId={treeId}
          person={selectedPerson}
          familyLinks={selectedPersonFamilyLinks}
          onClose={() => setShowEditPerson(false)}
          onAddExistingFamilyLink={() => {
            setShowEditPerson(false);
            setShowExistingPersonPicker(true);
          }}
          onAddFamilyLinkByType={(relationshipType) => {
            setShowEditPerson(false);
            setReturnToEditPersonAfterAddPerson(true);
            setPendingRelatedPersonSourceId(selectedPerson.id);
            setPendingRelationshipType(relationshipType);
            setPendingAddPersonPosition(null);
            setShowAddPerson(true);
          }}
          onJumpToFamilyLink={openRelationshipOnCanvas}
          onEditFamilyLink={openRelationshipOnCanvas}
          onSuccess={handleEditPersonSuccess}
        />
      )}


      {/* Edit Relationship Modal */}
      {selectedRelationship && showEditRelationshipModal && tree && (
        <EditRelationshipModal
          relationship={selectedRelationship}
          people={tree.people}
          onClose={() => {
            setShowEditRelationshipModal(false);
            setSelectedRelationship(null);
          }}
          onUpdated={() => {
            setShowEditRelationshipModal(false);
            setSelectedRelationship(null);
            // Reload tree to reflect relationship changes
            const loadTree = async () => {
              try {
                const response = await familyTreesAPI.getTree(treeId!);
                if (response.data.statusCode === 200) {
                  setTree(response.data.data);
                }
              } catch (err) {
              }
            };
            loadTree();
          }}
          onDeleted={() => {
            setShowEditRelationshipModal(false);
            setSelectedRelationship(null);
            // Reload tree to reflect relationship deletion
            const loadTree = async () => {
              try {
                const response = await familyTreesAPI.getTree(treeId!);
                if (response.data.statusCode === 200) {
                  setTree(response.data.data);
                }
              } catch (err) {
              }
            };
            loadTree();
          }}
        />
      )}
    </div>
    </MainLayout>
  );
};

// Person Details Panel Component
interface PersonDetailsPanelProps {
  person: PersonDto;
  canAddRelationship: boolean;
  onClose: () => void;
  onEdit: (person: PersonDto) => void;
  onAddRelationship: (person: PersonDto) => void;
  onDelete: (personId: string) => void;
}

const PersonDetailsPanel: React.FC<PersonDetailsPanelProps> = ({
  person,
  canAddRelationship,
  onClose,
  onEdit,
  onAddRelationship,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [isStoryExpanded, setIsStoryExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const formatDate = useFormatDate();
  const formattedBirthDate = person.dateOfBirth ? formatDate(person.dateOfBirth) : '';
  const formattedDeathDate = person.dateOfDeath ? formatDate(person.dateOfDeath) : '';
  const hasBiography = Boolean(person.shortBio?.trim() || person.longStory?.trim());
  const shortBio = person.shortBio?.trim() ?? '';
  const longStory = person.longStory?.trim() ?? '';
  const placeOfBirth = person.placeOfBirth?.trim() ?? '';
  const placeOfDeath = person.placeOfDeath?.trim() ?? '';
  const restingPlace = person.restingPlace?.trim() ?? '';
  const storyWordCount = longStory ? longStory.split(/\s+/).length : 0;
  const storyPreview = longStory.length > 280 ? `${longStory.slice(0, 280).trimEnd()}...` : longStory;
  const hasAddress = Boolean(person.address1 || person.address2 || person.city || person.state || person.zip || person.country);

  return (
    <div className="p-6" data-testid="person-details-panel">
      <div className="flex justify-between items-start mb-6">
        <h2 className={`text-xl font-bold ${brandClasses.primaryText}`} data-testid="person-details-name">
          {person.firstName} {person.lastName}
        </h2>
        <button
          onClick={onClose}
          className={`${brandClasses.subtleText} hover:text-[#4a453b]`}
        >
          ✕
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
              person.isDeceased
                ? 'border-[#D8C3B6] bg-[#F5ECE6] text-[#8A4B2F]'
                : 'border-[#C0DD97] bg-[#EAF3DE] text-[#3B6D11]'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${person.isDeceased ? 'bg-[#B06B4A]' : 'bg-[#639922]'}`} />
            {person.isDeceased ? 'Deceased' : 'Living'}
          </span>
        </div>

        {hasBiography && (
          <div className="rounded-2xl border border-[#e4ddcd] bg-[#fcfaf6] p-4">
            <label className={`text-[11px] font-medium uppercase tracking-[0.08em] ${brandClasses.bodyText}`}>
              Biography
            </label>
            {shortBio && (
              <p className="mt-2 text-sm font-medium leading-6 text-[#2f2a24]">
                {shortBio}
              </p>
            )}
            {longStory ? (
              <div className="mt-3 space-y-3">
                <p className={`text-sm leading-6 whitespace-pre-line ${brandClasses.mutedText}`}>
                  {isStoryExpanded ? longStory : storyPreview}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-[#e4ddcd] bg-white px-2.5 py-1 text-xs font-medium text-[#6b6560]">
                    {storyWordCount} {storyWordCount === 1 ? 'word' : 'words'}
                  </span>
                  {longStory.length > 280 && (
                    <button
                      type="button"
                      onClick={() => setIsStoryExpanded((current) => !current)}
                      className="rounded-full border border-[#d5cec3] bg-white px-3 py-1 text-xs font-medium text-[#185FA5] transition hover:bg-[#EEF4FB]"
                    >
                      {isStoryExpanded ? 'Show Less' : 'Read Story'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className={`mt-2 text-sm ${brandClasses.mutedText}`}>
                Add a longer story in the full profile to preserve milestones, memories, and family context.
              </p>
            )}
          </div>
        )}

        {formattedBirthDate && (
          <div>
            <label className={`text-sm font-medium ${brandClasses.bodyText}`}>Born</label>
            <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
              {formattedBirthDate}
              {placeOfBirth ? ` • ${placeOfBirth}` : ''}
            </p>
          </div>
        )}

        {formattedDeathDate && (
          <div>
            <label className={`text-sm font-medium ${brandClasses.bodyText}`}>Died</label>
            <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
              {formattedDeathDate}
              {placeOfDeath ? ` • ${placeOfDeath}` : ''}
            </p>
          </div>
        )}

        {restingPlace && (
          <div>
            <label className={`text-sm font-medium ${brandClasses.bodyText}`}>Resting Place</label>
            <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{restingPlace}</p>
          </div>
        )}

        {hasAddress && (
          <div>
            <label className={`text-sm font-medium ${brandClasses.bodyText}`}>Address</label>
            <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
              {person.address1 && <>{person.address1}</>}
              {person.address2 && <>, {person.address2}</>}
              {(person.city || person.state || person.zip) && (
                <>
                  <br />
                  {person.city && <>{person.city}</>}
                  {person.state && <>{person.city ? ', ' : ''}{person.state}</>}
                  {person.zip && <> {person.zip}</>}
                </>
              )}
              {person.country && (
                <>
                  <br />
                  {person.country}
                </>
              )}
            </p>
          </div>
        )}

        {person.phone && (
          <div>
            <label className={`text-sm font-medium ${brandClasses.bodyText}`}>Phone</label>
            <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{person.phone}</p>
          </div>
        )}

        {(person.canEdit || canAddRelationship) && (
          <div className="space-y-2 border-t border-[#e4ddcd] pt-4">
            {canAddRelationship && (
              <button
                onClick={() => onAddRelationship(person)}
                data-testid="person-details-add-relationship-button"
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm font-medium"
              >
                {t('personPanel.addRelation', { ns: 'canvas' })}
              </button>
            )}
            {person.canEdit && (
              <button
                onClick={() => onEdit(person)}
                data-testid="person-details-edit-button"
                className="w-full rounded-md bg-[#7f8d57] px-4 py-2 text-sm font-medium text-white hover:bg-[#6f7d49]"
              >
                {t('personPanel.editPerson', { ns: 'canvas' })}
              </button>
            )}
          </div>
        )}

        {person.canDelete && (
          <div className="pt-2">
            {confirmDelete ? (
              <div className="rounded-2xl border border-[#F1C1C1] bg-[#FFF5F5] p-4">
                <p className="text-sm font-semibold text-[#8C2F2F]">{t('personPanel.deleteConfirmTitle', { ns: 'canvas' })}</p>
                <p className="mt-1 text-sm text-[#6B6560]">
                  {t('personPanel.deleteConfirmMessage', { ns: 'canvas' })}
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => onDelete(person.id)}
                    data-testid="confirm-delete-button"
                    className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    {t('personPanel.deleteConfirmButton', { ns: 'canvas' })}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="flex-1 rounded-md border border-[#D7D3CC] bg-white px-4 py-2 text-sm font-medium text-[#6B6560] hover:bg-[#F9F7F4]"
                  >
                    {t('buttons.cancel', { ns: 'common' })}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="person-details-delete-button"
                className="w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                {t('personPanel.deletePerson', { ns: 'canvas' })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
